import React, { useState, useEffect, useCallback, useRef } from 'react';
import { authAPI, mastersAPI, applicationsAPI, adminAPI } from './services/api';
import toast, { Toaster } from 'react-hot-toast';
import './App.css';

// ── Auth Context ───────────────────────────────────────────
const AuthContext = React.createContext(null);
const useAuth = () => React.useContext(AuthContext);

// ── Error Boundary — catches any unhandled JS crash ──────────
class ErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { hasError: false, error: null }; }
  static getDerivedStateFromError(error) { return { hasError: true, error }; }
  componentDidCatch(error, info) { console.error('App crashed:', error, info); }
  render() {
    if (!this.state.hasError) return this.props.children;
    return (
      <div style={{
        minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: '#f5f5f5', padding: 24,
      }}>
        <div style={{
          background: '#fff', borderRadius: 16, padding: '40px 36px', maxWidth: 480,
          textAlign: 'center', boxShadow: '0 8px 32px rgba(0,0,0,.12)',
        }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>⚠️</div>
          <h2 style={{ color: '#c62828', fontSize: 20, fontWeight: 800, marginBottom: 12 }}>
            Something went wrong
          </h2>
          <p style={{ color: '#757575', fontSize: 14, marginBottom: 24, lineHeight: 1.6 }}>
            An unexpected error occurred. Your data is safe. Please reload the page to continue.
          </p>
          <button
            onClick={() => window.location.reload()}
            style={{
              background: '#004d40', color: '#fff', border: 'none', borderRadius: 8,
              padding: '12px 28px', fontSize: 14, fontWeight: 700, cursor: 'pointer',
              fontFamily: 'inherit',
            }}
          >🔄 Reload Page</button>
          {process.env.NODE_ENV !== 'production' && this.state.error && (
            <pre style={{ marginTop: 20, fontSize: 11, color: '#c62828', textAlign: 'left', overflow: 'auto' }}>
              {this.state.error.toString()}
            </pre>
          )}
        </div>
      </div>
    );
  }
}

// ── Utility ───────────────────────────────────────────────
// Service period calculated from reported date to cutoff: 31-Dec-2025
const CUTOFF_DATE = new Date('2025-12-31');

// ── Application submission deadline ──────────────────────
const DEADLINE = new Date('2026-05-19T20:00:00+05:30'); // 8:00 PM IST 19-05-2026
const isDeadlinePassed = () => new Date() >= DEADLINE;

const calcServicePeriod = (dateStr) => {
  if (!dateStr) return null;
  const from = new Date(dateStr);
  if (from > CUTOFF_DATE) return null;  // future date beyond cutoff — invalid
  let years  = CUTOFF_DATE.getFullYear() - from.getFullYear();
  let months = CUTOFF_DATE.getMonth()    - from.getMonth();
  let days   = CUTOFF_DATE.getDate()     - from.getDate();
  if (days < 0) {
    months--;
    days += new Date(CUTOFF_DATE.getFullYear(), CUTOFF_DATE.getMonth(), 0).getDate();
  }
  if (months < 0) { years--; months += 12; }
  return { years, months, days };
};

// ── Date helper: strip ISO timestamp to yyyy-MM-dd ─────────
const toDateStr = (val) => {
  if (!val) return '';
  if (typeof val === 'string') return val.split('T')[0];
  if (val instanceof Date) return val.toISOString().split('T')[0];
  return String(val);
};

// ═══════════════════════════════════════════════════════════
//  ROOT APP
// ═══════════════════════════════════════════════════════════
export default function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [screen, setScreen] = useState('login'); // login | change-password | dept-select | notice | employee | admin

  useEffect(() => {
    const token = localStorage.getItem('tg_token');
    const stored = localStorage.getItem('tg_user');
    if (token && stored) {
      const u = JSON.parse(stored);
      setUser(u);
      setScreen(u.isFirstLogin ? 'change-password' : (
  u.role === 'employee' ? (isDeadlinePassed() ? (u.hasSubmitted ? 'employee' : 'closed') : (u.deptSelected ? 'employee' : 'dept-select')) :
  u.role === 'scrutiny_cdma' ? 'scrutiny_cdma' :
  u.role === 'scrutiny_dtcp' ? 'scrutiny_dtcp' :
  u.role === 'district_officer' ? 'district_officer' :
  'admin'
));
    }
    setLoading(false);
  }, []);

  const login = async (employeeId, password) => {
    const res = await authAPI.login({ employeeId, password });
    const { token, user: u } = res.data;
    // Always clear localStorage first — removes any stale dept/token data
    localStorage.clear();
    const freshUser = { ...u, deptSelected: false };
    localStorage.setItem('tg_token', token);
    localStorage.setItem('tg_user', JSON.stringify(freshUser));
    setUser(freshUser);
    setScreen(freshUser.isFirstLogin ? 'change-password' : (
  freshUser.role === 'employee' ? (isDeadlinePassed() ? (freshUser.hasSubmitted ? 'dept-select' : 'closed') : 'dept-select') :
  freshUser.role === 'scrutiny_cdma' ? 'scrutiny_cdma' :
  freshUser.role === 'scrutiny_dtcp' ? 'scrutiny_dtcp' :
  freshUser.role === 'district_officer' ? 'district_officer' :
  'admin'
));
    return freshUser;
  };

  const logout = () => {
    localStorage.clear();
    setUser(null);
    setScreen('login');
  };

  const afterPasswordChange = () => {
    const updated = { ...user, isFirstLogin: false, deptSelected: false };
    localStorage.setItem('tg_user', JSON.stringify(updated));
    setUser(updated);
    setScreen(updated.role === 'employee' ? 'dept-select' : 'admin');
  };

  if (loading) return <LoadingScreen />;

  return (
    <ErrorBoundary>
    <AuthContext.Provider value={{ user, login, logout }}>
      <Toaster position="top-right" toastOptions={{
        style: { fontFamily: 'inherit', fontSize: '14px' },
        duration: 4000,
        success: { duration: 3000, iconTheme: { primary: '#2e7d32', secondary: '#fff' } },
        error:   { duration: 10000, iconTheme: { primary: '#c62828', secondary: '#fff' } },
      }} />
      {screen === 'login' && <LoginScreen onLogin={login} />}
      {screen === 'change-password' && <ChangePasswordScreen onDone={afterPasswordChange} onLogout={logout} />}
      {screen === 'dept-select' && (
        <DeptSelectScreen
          onConfirm={(deptId, deptName) => {
            // Mark dept selected in localStorage so refresh skips back to employee
            const updated = {
              ...user,
              deptSelected: true,
              selectedDeptId: deptId,    // mirrors user.deptId — stored for convenience
              selectedDeptName: deptName,
            };
            localStorage.setItem('tg_user', JSON.stringify(updated));
            setUser(updated);
            setScreen('notice');
          }}
          onLogout={logout}
        />
      )}
      {screen === 'notice' && (
        <EligibilityNoticeScreen
          onConfirm={() => setScreen('employee')}
          onLogout={logout}
        />
      )}
      {screen === 'closed' && <ClosedScreen />}
      {screen === 'employee' && <EmployeePortal onLogout={logout} />}
      {screen === 'scrutiny_cdma' && <ScrutinyOfficerPage dept="CDMA" onLogout={logout} />}
      {screen === 'scrutiny_dtcp' && <ScrutinyOfficerPage dept="DTCP" onLogout={logout} />}
      {screen === 'district_officer' && <DistrictOfficerPage onLogout={logout} />}
      {screen === 'admin' && <AdminPanel onLogout={logout} />}
    </AuthContext.Provider>
    </ErrorBoundary>
  );
}

// ═══════════════════════════════════════════════════════════
//  LOADING SCREEN
// ═══════════════════════════════════════════════════════════
function LoadingScreen({ message = "Loading..." }) {
  return (
    <div className="loading-screen">
      <div className="spinner" />
      <p>Loading Telangana Transfer Portal...</p>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
//  LOGIN SCREEN
// ═══════════════════════════════════════════════════════════
// ══════════════════════════════════════════════════════════
//  DEPARTMENT SELECTION SCREEN
// ══════════════════════════════════════════════════════════
function DeptSelectScreen({ onConfirm, onLogout }) {
  const { user } = useAuth();

  // Department is derived from the user's account (users.dept_id in DB)
  // No manual selection needed — just confirm and proceed
  const deptId   = String(user?.deptId   || '');
  const deptName = user?.deptName || user?.deptCode || 'Your Department';

  const handleConfirm = () => {
    // Proceed even if deptId is empty — some accounts may not have dept assigned
    onConfirm(deptId || '', deptName || '');
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(140deg, #004d40 0%, #00695c 55%, #00897b 100%)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
    }}>
      <div style={{
        background: '#fff', borderRadius: 20, padding: '40px 36px',
        width: '100%', maxWidth: 500,
        boxShadow: '0 20px 60px rgba(0,0,0,.25)',
      }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: 28 }}>
          <div style={{
            width: 64, height: 64, borderRadius: '50%',
            background: 'var(--teal-lt)', display: 'flex',
            alignItems: 'center', justifyContent: 'center',
            margin: '0 auto 16px', fontSize: 30,
          }}>🏛</div>
          <h2 style={{ color: 'var(--teal-dark)', fontSize: 20, fontWeight: 800, marginBottom: 6 }}>
            Confirm Your Department
          </h2>
          <p style={{ color: 'var(--text-muted)', fontSize: 13, lineHeight: 1.6 }}>
            Welcome, <strong>{user?.employeeId}</strong>.<br />
            Please confirm your department to proceed to the Transfer Portal.
          </p>
        </div>

        {/* Department info — derived from user account (DB) */}
        <div style={{ marginBottom: 24, padding: '14px 16px', background: 'var(--teal-lt)', borderRadius: 8, border: '1.5px solid #80cbc4' }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: .5, marginBottom: 6 }}>
            Your Department
          </div>
          <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--teal-dark)' }}>
            🏛 {deptName}
          </div>
          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
            As per your account registration (Dept ID: {deptId})
          </div>
        </div>

        <button
          className="btn btn-primary btn-full"
          onClick={handleConfirm}
          disabled={false}
          style={{ fontSize: 15, padding: '12px 0', marginBottom: 12 }}
        >
          Confirm & Continue →
        </button>
        <button
          type="button"
          className="btn btn-secondary btn-full"
          onClick={onLogout}
          style={{ fontSize: 13 }}
        >
          ← Back to Login
        </button>

        <p style={{ marginTop: 16, fontSize: 11.5, color: 'var(--text-muted)', textAlign: 'center', lineHeight: 1.6 }}>
          Government of Telangana · MA Department · General Transfers 2026
        </p>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════
//  ELIGIBILITY NOTICE SCREEN
// ══════════════════════════════════════════════════════════
function EligibilityNoticeScreen({ onConfirm, onLogout }) {
  const [agreed, setAgreed] = useState(false);

  const [notRequired, setNotRequired] = useState(false);

  const rules = [
    {
      icon: '📌',
      color: '#c62828',
      bg: '#ffebee',
      border: '#ef9a9a',
      label: 'Compulsory Transfer',
      text: 'Employees who have completed more than 4 years in the present working station are required to exercise this transfer option compulsorily.',
    },
    {
      icon: '📋',
      color: '#e65100',
      bg: '#fff3e0',
      border: '#ffcc80',
      label: 'Voluntary Transfer',
      text: 'Employees who have completed between 3 to 4 years in the present working station may exercise this option on voluntary request basis.',
    },
    {
      icon: '👫',
      color: '#1565c0',
      bg: '#e3f2fd',
      border: '#90caf9',
      label: 'Spouse Grounds',
      text: 'Employees applying under Spouse Grounds may exercise this option irrespective of the tenure in the present working station.',
    },
  ];

  // Both checkboxes must be checked to proceed
  const canProceed = agreed && notRequired;

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(140deg, #004d40 0%, #00695c 55%, #00897b 100%)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: 16,
    }}>
      <div style={{
        background: '#fff', borderRadius: 20, width: '100%', maxWidth: 620,
        boxShadow: '0 20px 60px rgba(0,0,0,.25)',
        overflow: 'hidden',
      }}>
        {/* Header */}
        <div style={{
          background: 'linear-gradient(135deg, #004d40, #00695c)',
          padding: '24px 32px', borderBottom: '4px solid #f9a825',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            <span style={{ fontSize: 36 }}>📢</span>
            <div>
              <h2 style={{ color: '#fff', fontSize: 18, fontWeight: 800, marginBottom: 4 }}>
                Employee Transfer Counselling
              </h2>
              <p style={{ color: 'rgba(255,255,255,.8)', fontSize: 12, margin: 0 }}>
                Eligibility Notice — General Transfers 2026 · MA Department
              </p>
            </div>
          </div>
        </div>

        {/* Rules */}
        <div style={{ padding: '24px 28px' }}>
          <p style={{ fontSize: 13, color: '#546e7a', marginBottom: 20, lineHeight: 1.6 }}>
            Please read the following eligibility criteria carefully before proceeding with your transfer option form.
          </p>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {rules.map((r, i) => (
              <div key={i} style={{
                display: 'flex', gap: 14, padding: '14px 16px',
                background: r.bg, borderRadius: 10,
                border: `1.5px solid ${r.border}`,
                borderLeft: `4px solid ${r.color}`,
              }}>
                <span style={{ fontSize: 22, flexShrink: 0, marginTop: 1 }}>{r.icon}</span>
                <div>
                  <div style={{ fontSize: 12, fontWeight: 800, color: r.color, textTransform: 'uppercase', letterSpacing: .5, marginBottom: 4 }}>
                    {r.label}
                  </div>
                  <p style={{ fontSize: 13, color: '#333', margin: 0, lineHeight: 1.6 }}>{r.text}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Not Required + Important Notice — two-part red card */}
          <div
            onClick={() => setNotRequired(v => !v)}
            style={{
              display: 'flex', alignItems: 'flex-start', gap: 14, cursor: 'pointer',
              marginTop: 12, padding: '16px 16px',
              background: notRequired ? '#ffebee' : '#fff5f5',
              borderRadius: 10,
              border: `1.5px solid ${notRequired ? '#c62828' : '#ffcdd2'}`,
              borderLeft: `4px solid #c62828`,
              transition: 'all .2s',
              userSelect: 'none',
            }}
          >
            <input
              type="checkbox"
              checked={notRequired}
              onChange={() => {}}
              onClick={e => e.stopPropagation()}
              style={{ width: 18, height: 18, flexShrink: 0, marginTop: 3, accentColor: '#c62828', cursor: 'pointer' }}
            />
            <div style={{ flex: 1 }}>
              {/* Important Notice */}
              <div style={{
                padding: '10px 14px',
                background: '#c62828',
                borderRadius: 7,
              }}>
                <div style={{ fontSize: 12, fontWeight: 800, color: '#fff', textTransform: 'uppercase', letterSpacing: .6, marginBottom: 5 }}>
                  ⚠ Important Notice
                </div>
                <p style={{ fontSize: 13, color: '#fff', margin: 0, lineHeight: 1.65, fontWeight: 500 }}>
                  Employees other than above 3 categories are not eligible for transfers. Hence need not submit this option form.
                </p>
              </div>
            </div>
          </div>

          <div style={{ height: 1, background: '#e0e0e0', margin: '16px 0' }} />

          {/* Agreement checkbox — for eligible employees */}
          <div
            onClick={() => setAgreed(v => !v)}
            style={{
              display: 'flex', alignItems: 'flex-start', gap: 12, cursor: 'pointer',
              padding: '14px 16px',
              background: agreed ? '#f1f8f5' : '#fafafa',
              borderRadius: 8,
              border: `1.5px solid ${agreed ? '#80cbc4' : '#e0e0e0'}`,
              transition: 'all .2s',
              userSelect: 'none',
            }}
          >
            <input
              type="checkbox"
              checked={agreed}
              onChange={() => {}}
              onClick={e => e.stopPropagation()}
              style={{ width: 18, height: 18, flexShrink: 0, marginTop: 2, accentColor: '#00695c', cursor: 'pointer' }}
            />
            <span style={{ fontSize: 13, color: '#333', lineHeight: 1.6 }}>
              I have read and understood the above eligibility criteria. I confirm that I am eligible to proceed with this transfer option form.
            </span>
          </div>

          {/* Proceed button — enabled when either checkbox is selected */}
          <div style={{ display: 'flex', gap: 12, marginTop: 20 }}>
            <button
              type="button"
              onClick={onLogout}
              style={{
                flex: 1, padding: '11px 0', borderRadius: 9, border: '1.5px solid #ddd',
                background: '#fff', cursor: 'pointer', fontSize: 13.5, fontWeight: 600,
                color: '#546e7a', fontFamily: 'inherit',
              }}
            >
              ← Back to Login
            </button>
            <button
              onClick={onConfirm}
              disabled={!canProceed}
              style={{
                flex: 2, padding: '11px 0', borderRadius: 9, border: 'none',
                background: canProceed ? 'var(--teal)' : '#e0e0e0',
                color: canProceed ? '#fff' : '#9e9e9e',
                cursor: canProceed ? 'pointer' : 'not-allowed',
                fontSize: 14, fontWeight: 700, fontFamily: 'inherit',
                transition: 'all .2s',
                boxShadow: canProceed ? '0 4px 14px rgba(0,105,92,.35)' : 'none',
              }}
            >
              I Understand & Proceed to Form →
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function LoginScreen({ onLogin }) {
  const [empId, setEmpId] = useState('');
  const [password, setPassword] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!empId.trim()) {
      setError('Please enter your Employee ID.');
      return;
    }
    if (!password) {
      setError('Please enter your Password.');
      return;
    }

    setLoading(true);
    try {
      await onLogin(empId.trim(), password);
    } catch (err) {
      const msg = err.response?.data?.error || '';
      if (msg.toLowerCase().includes('invalid') || msg.toLowerCase().includes('incorrect')) {
        setError('Invalid Employee ID or Password. Please try again.');
      } else if (msg.toLowerCase().includes('inactive')) {
        setError('Your account is inactive. Please contact the IT Helpdesk.');
      } else if (msg.toLowerCase().includes('locked')) {
        setError('Account locked due to multiple failed attempts. Try again after 15 minutes.');
      } else if (msg.toLowerCase().includes('too many') || err.response?.status === 429) {
        setError('Too many login attempts. Please wait 15 minutes before trying again.');
      } else {
        setError(msg || 'Login failed. Please check your credentials and try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  // Clear error when user starts typing
  const handleEmpIdChange = (e) => {
    setEmpId(e.target.value.toUpperCase());
    if (error) setError('');
  };
  const handlePasswordChange = (e) => {
    setPassword(e.target.value);
    if (error) setError('');
  };

  // Auto-dismiss countdown
  const [countdown, setCountdown] = useState(10);
  useEffect(() => {
    if (!error) { setCountdown(10); return; }
    const interval = setInterval(() => setCountdown(c => c - 1), 1000);
    return () => clearInterval(interval);
  }, [error]);

  return (
    <div style={{
      minHeight: '100vh', display: 'flex',
      background: '#004d40',
    }}>
      {/* ── Left Panel — Branding ── */}
      <div style={{
        flex: 1, display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center',
        padding: '48px 40px', gap: 28,
        background: '#004d40',
      }}>
        {/* Logos */}
        <div style={{ display: 'flex', gap: 20, alignItems: 'center' }}>
          <div style={{
            width: 88, height: 88, borderRadius: '50%',
            background: '#fff', overflow: 'hidden',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 4px 20px rgba(0,0,0,.25)',
            padding: 4,
          }}>
            <img src="/assets/telangana-logo.png" alt="Government of Telangana"
              style={{ width: 76, height: 76, objectFit: 'contain' }}
              onError={e => { e.target.style.display='none'; e.target.parentNode.innerHTML='<span style="font-size:40px">🏛</span>'; }}
            />
          </div>
          <div style={{
            width: 88, height: 88, borderRadius: '50%',
            background: '#fff', overflow: 'hidden',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 4px 20px rgba(0,0,0,.25)',
            padding: 4,
          }}>
            <img src="/assets/rising-logo.png" alt="Telangana Rising"
              style={{ width: 76, height: 76, objectFit: 'contain' }}
              onError={e => { e.target.style.display='none'; e.target.parentNode.innerHTML='<span style="font-size:40px">⭐</span>'; }}
            />
          </div>
        </div>

        {/* Title */}
        <div style={{ textAlign: 'center' }}>
          <h1 style={{ color: '#fff', fontSize: 36, fontWeight: 800, margin: '0 0 12px', letterSpacing: .5 }}>
            General Transfers 2026
          </h1>
          <p style={{ color: 'rgba(255,255,255,.9)', fontSize: 18, margin: '0 0 6px', fontWeight: 600 }}>
            Municipal Administration Department
          </p>
          <p style={{ color: 'rgba(255,255,255,.75)', fontSize: 16, margin: 0 }}>
            Government of Telangana
          </p>
        </div>

        {/* GO Reference card */}
        <div style={{
          padding: '18px 28px', borderRadius: 12,
          background: 'rgba(255,255,255,.1)',
          border: '1px solid rgba(255,255,255,.2)',
          maxWidth: 340, textAlign: 'center',
        }}>
          <p style={{ color: 'rgba(255,255,255,.95)', fontSize: 14.5, fontWeight: 700, margin: '0 0 8px' }}>
            G.O.Ms.No.38, Finance (HRM-I) Department
          </p>
          <p style={{ color: 'rgba(255,255,255,.8)', fontSize: 14, margin: '0 0 6px' }}>
            Dated: 21.04.2026
          </p>
          <p style={{ color: 'rgba(255,255,255,.75)', fontSize: 13.5, margin: 0 }}>
            Employee Transfer Option Form — Annexure II
          </p>
        </div>
      </div>

      {/* ── Right Panel — Login Form ── */}
      <div style={{
        flex: 1, background: '#fff',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '40px 32px',
        minHeight: '100vh',
      }}>
        <div style={{ width: '100%', maxWidth: 380 }}>

          {/* Heading */}
          <div style={{ marginBottom: 28 }}>
            <h2 style={{ fontSize: 24, fontWeight: 800, color: '#004d40', margin: '0 0 8px' }}>
              Employee Login
            </h2>
            <p style={{ fontSize: 13, color: '#757575', margin: 0, lineHeight: 1.6 }}>
              Enter your Employee ID and password to access the Transfer Portal
            </p>
          </div>

          {/* Error banner with countdown */}
          {error && (
            <div className="login-error-box" style={{ marginBottom: 18 }}>
              <span style={{ fontSize: 16, flexShrink: 0 }}>⚠</span>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 700, marginBottom: 2 }}>{error}</div>
                <div style={{ fontSize: 11, opacity: .8 }}>
                  Dismissing in {countdown}s
                  <div style={{
                    height: 3, background: '#c62828', borderRadius: 2, marginTop: 4,
                    width: `${(countdown / 10) * 100}%`, transition: 'width 1s linear',
                  }} />
                </div>
              </div>
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit}>

            {/* Employee ID */}
            <div style={{ marginBottom: 16 }}>
              <label style={{
                fontSize: 11, fontWeight: 700, color: '#555',
                textTransform: 'uppercase', letterSpacing: .6,
                display: 'block', marginBottom: 6,
              }}>Employee ID</label>
              <div style={{ position: 'relative' }}>
                <span style={{
                  position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)',
                  fontSize: 16, color: '#9e9e9e',
                }}>👤</span>
                <input
                  type="text"
                  placeholder="Enter Employee ID"
                  value={empId}
                  onChange={handleEmpIdChange}
                  autoComplete="username"
                  required
                  style={{
                    width: '100%', padding: '11px 12px 11px 40px',
                    borderRadius: 9, border: `1.5px solid ${error && !empId.trim() ? '#c62828' : '#e0e0e0'}`,
                    fontSize: 14, fontFamily: 'inherit', boxSizing: 'border-box',
                    outline: 'none', transition: 'border .2s',
                  }}
                />
              </div>
            </div>

            {/* Password */}
            <div style={{ marginBottom: 24 }}>
              <label style={{
                fontSize: 11, fontWeight: 700, color: '#555',
                textTransform: 'uppercase', letterSpacing: .6,
                display: 'block', marginBottom: 6,
              }}>Password</label>
              <div style={{ position: 'relative' }}>
                <span style={{
                  position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)',
                  fontSize: 16, color: '#9e9e9e',
                }}>🔒</span>
                <input
                  type={showPwd ? 'text' : 'password'}
                  placeholder="Enter Password"
                  value={password}
                  onChange={handlePasswordChange}
                  autoComplete="current-password"
                  required
                  style={{
                    width: '100%', padding: '11px 44px 11px 40px',
                    borderRadius: 9, border: `1.5px solid ${error && !password ? '#c62828' : '#e0e0e0'}`,
                    fontSize: 14, fontFamily: 'inherit', boxSizing: 'border-box',
                    outline: 'none', transition: 'border .2s',
                  }}
                />
                <button
                  type="button"
                  onClick={() => setShowPwd(v => !v)}
                  style={{
                    position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)',
                    background: 'none', border: 'none', cursor: 'pointer', fontSize: 16, color: '#9e9e9e', padding: 0,
                  }}
                >{showPwd ? '🙈' : '👁️'}</button>
              </div>
            </div>

            {/* Sign In Button */}
            <button
              type="submit"
              disabled={loading}
              style={{
                width: '100%', padding: '12px 0',
                background: loading ? '#9e9e9e' : '#004d40',
                color: '#fff', border: 'none', borderRadius: 9,
                fontSize: 15, fontWeight: 700, fontFamily: 'inherit',
                cursor: loading ? 'not-allowed' : 'pointer',
                transition: 'background .2s',
                boxShadow: loading ? 'none' : '0 4px 14px rgba(0,77,64,.3)',
              }}
            >
              {loading ? '⏳ Signing in…' : '→ Sign In'}
            </button>
          </form>

          {/* Footer */}
          <div style={{
            marginTop: 24, paddingTop: 18,
            borderTop: '1px solid #f0f0f0',
            textAlign: 'center',
          }}>
            <p style={{ fontSize: 12.5, color: '#9e9e9e', margin: '0 0 6px' }}>
              Default password: <strong style={{ color: '#004d40' }}>TG@[EmployeeID]</strong>
            </p>
            <p style={{ fontSize: 12.5, color: '#9e9e9e', margin: 0 }}>
              Contact IT Helpdesk for access issues
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
//  CHANGE PASSWORD SCREEN
// ═══════════════════════════════════════════════════════════
function ChangePasswordScreen({ onDone, onLogout }) {
  const [form, setForm] = useState({ current: '', newPwd: '', confirm: '' });
  const [loading, setLoading] = useState(false);
  const [strength, setStrength] = useState(0);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const calcStrength = (pwd) => {
    let s = 0;
    if (pwd.length >= 8) s++;
    if (/[A-Z]/.test(pwd)) s++;
    if (/[a-z]/.test(pwd)) s++;
    if (/\d/.test(pwd)) s++;
    if (/[@$!%*?&]/.test(pwd)) s++;
    return s;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.newPwd !== form.confirm) { toast.error('Passwords do not match'); return; }
    if (strength < 4) { toast.error('Password is too weak'); return; }
    setLoading(true);
    try {
      await authAPI.changePassword({ currentPassword: form.current, newPassword: form.newPwd });
      toast.success('Password changed successfully!');
      onDone();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to change password');
    } finally {
      setLoading(false);
    }
  };

  const strengthColors = ['#e0e0e0', '#c62828', '#ff6f00', '#f9a825', '#2e7d32', '#1b5e20'];
  const strengthLabels = ['', 'Very Weak', 'Weak', 'Fair', 'Strong', 'Very Strong'];

  return (
    <div className="changepwd-page">
      <div className="changepwd-box">
        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          <span style={{ fontSize: 48 }}>🔑</span>
          <h2 style={{ color: '#004d40', marginTop: 8 }}>Set New Password</h2>
          <p style={{ color: '#757575', fontSize: 13 }}>First login — please change your default password</p>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="form-group" style={{ marginBottom: 16 }}>
            <label>Current Password</label>
            <input type="password" value={form.current} onChange={e => setForm(f => ({ ...f, current: e.target.value }))} required />
          </div>
          <div className="form-group" style={{ marginBottom: 8 }}>
            <label>New Password</label>
            <div style={{ position: 'relative' }}>
              <input type={showNew ? 'text' : 'password'} value={form.newPwd}
                onChange={e => { setForm(f => ({ ...f, newPwd: e.target.value })); setStrength(calcStrength(e.target.value)); }}
                required style={{ paddingRight: 40 }} />
              <button type="button" onClick={() => setShowNew(v => !v)}
                style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#757575', fontSize: 16 }}>
                {showNew ? '🙈' : '👁️'}
              </button>
            </div>
          </div>
          {form.newPwd && (
            <div style={{ marginBottom: 16 }}>
              <div style={{ display: 'flex', gap: 4, marginBottom: 4 }}>
                {[1, 2, 3, 4, 5].map(i => (
                  <div key={i} style={{ flex: 1, height: 4, borderRadius: 2, background: i <= strength ? strengthColors[strength] : '#e0e0e0', transition: 'background .3s' }} />
                ))}
              </div>
              <p style={{ fontSize: 11, color: strengthColors[strength] }}>{strengthLabels[strength]}</p>
              <ul style={{ fontSize: 11, color: '#757575', margin: '6px 0 0 16px' }}>
                <li style={{ color: form.newPwd.length >= 8 ? '#2e7d32' : '#757575' }}>At least 8 characters</li>
                <li style={{ color: /[A-Z]/.test(form.newPwd) ? '#2e7d32' : '#757575' }}>Uppercase letter</li>
                <li style={{ color: /\d/.test(form.newPwd) ? '#2e7d32' : '#757575' }}>Number</li>
                <li style={{ color: /[@$!%*?&]/.test(form.newPwd) ? '#2e7d32' : '#757575' }}>Special character (@$!%*?&)</li>
              </ul>
            </div>
          )}
          <div className="form-group" style={{ marginBottom: 24 }}>
            <label>Confirm New Password</label>
            <div style={{ position: 'relative' }}>
              <input type={showConfirm ? 'text' : 'password'} value={form.confirm}
                onChange={e => setForm(f => ({ ...f, confirm: e.target.value }))}
                className={form.confirm && form.newPwd !== form.confirm ? 'error' : ''}
                required style={{ paddingRight: 40 }} />
              <button type="button" onClick={() => setShowConfirm(v => !v)}
                style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#757575', fontSize: 16 }}>
                {showConfirm ? '🙈' : '👁️'}
              </button>
            </div>
            {form.confirm && form.newPwd !== form.confirm && <span className="field-error">Passwords do not match</span>}
          </div>
          <button className="btn btn-primary btn-full" disabled={loading}>{loading ? 'Updating…' : 'Set New Password'}</button>
          <button type="button" className="btn btn-secondary btn-full" style={{ marginTop: 10 }} onClick={onLogout}>← Back to Login</button>
        </form>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════
//  REUSABLE APP MODAL
// ══════════════════════════════════════════════════════════
function AppModal({ modal, onClose }) {
  if (!modal) return null;
  const { type = 'info', title, message, lines = [], onConfirm, confirmLabel = 'OK', cancelLabel } = modal;

  const icons   = { info: '💡', warn: '⚠️', error: '🚫', success: '✅', block: '🛑' };
  const colors  = { info: '#1565c0', warn: '#e65100', error: '#c62828', success: '#2e7d32', block: '#c62828' };
  const bgTop   = { info: '#e3f2fd', warn: '#fff3e0', error: '#ffebee', success: '#e8f5e9', block: '#ffebee' };
  const col = colors[type] || colors.info;
  const bg  = bgTop[type]  || bgTop.info;

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,.45)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 9999, padding: 16, backdropFilter: 'blur(2px)',
    }} onClick={onClose}>
      <div style={{
        background: '#fff', borderRadius: 16, width: '100%', maxWidth: 440,
        boxShadow: '0 20px 60px rgba(0,0,0,.25)', overflow: 'hidden',
        animation: 'modalIn .2s ease',
      }} onClick={e => e.stopPropagation()}>

        {/* Coloured header strip */}
        <div style={{ background: bg, padding: '20px 24px 16px', borderBottom: `3px solid ${col}` }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <span style={{ fontSize: 32, lineHeight: 1 }}>{icons[type]}</span>
            <div>
              <div style={{ fontSize: 16, fontWeight: 800, color: col, lineHeight: 1.2 }}>{title}</div>
            </div>
          </div>
        </div>

        {/* Body */}
        <div style={{ padding: '18px 24px 20px' }}>
          {message && (
            <p style={{ fontSize: 14, color: '#333', lineHeight: 1.7, marginBottom: lines.length ? 14 : 0 }}>
              {message}
            </p>
          )}
          {lines.length > 0 && (
            <ul style={{ margin: 0, paddingLeft: 20, display: 'flex', flexDirection: 'column', gap: 5 }}>
              {lines.map((l, i) => (
                <li key={i} style={{ fontSize: 13, color: '#555', lineHeight: 1.5 }}>{l}</li>
              ))}
            </ul>
          )}
        </div>

        {/* Footer buttons */}
        <div style={{
          display: 'flex', gap: 10, justifyContent: 'flex-end',
          padding: '12px 24px 18px', borderTop: '1px solid #f0f0f0',
        }}>
          {cancelLabel && (
            <button onClick={onClose} style={{
              padding: '8px 20px', borderRadius: 8, border: '1.5px solid #ddd',
              background: '#fff', cursor: 'pointer', fontSize: 13.5, fontWeight: 600,
              color: '#555', fontFamily: 'inherit',
            }}>{cancelLabel}</button>
          )}
          <button onClick={() => { onConfirm?.(); onClose(); }} style={{
            padding: '8px 22px', borderRadius: 8, border: 'none',
            background: col, color: '#fff', cursor: 'pointer',
            fontSize: 13.5, fontWeight: 700, fontFamily: 'inherit',
            boxShadow: `0 3px 10px ${col}55`,
          }}>{confirmLabel}</button>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
//  EMPLOYEE PORTAL
// ═══════════════════════════════════════════════════════════
// ── Submitted Screen ──────────────────────────────────────
function SubmittedScreen({ application, form, onLogout, deadlinePassed }) {
  const [pdfLoading, setPdfLoading] = React.useState(false);
  const [transferOrder, setTransferOrder] = React.useState(null);
  const [orderDownloading, setOrderDownloading] = React.useState(false);

  // Fetch the employee's transfer order on mount (if one has been issued)
  React.useEffect(() => {
    const fetchOrder = async () => {
      try {
        const token = localStorage.getItem('tg_token');
        const res = await fetch('/api/v1/employee/my-transfer-order', {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          setTransferOrder(data);
        }
      } catch (e) { /* no order is fine */ }
    };
    fetchOrder();
  }, []);

  const handleDownloadPDF = async () => {
    if (!application?.id) { toast.error('Application ID not found'); return; }
    setPdfLoading(true);
    try {
      const token = localStorage.getItem('tg_token');
      const res = await fetch(`/api/v1/applications/${application.id}/pdf`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('PDF generation failed');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Transfer_Application_${form.employee_id_display || 'TG'}_2026.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('PDF downloaded successfully');
    } catch (err) {
      toast.error('PDF generation failed. Please try again.');
    } finally {
      setPdfLoading(false);
    }
  };

  const downloadTransferOrder = async () => {
    if (orderDownloading) return;
    setOrderDownloading(true);
    try {
      const token = localStorage.getItem('tg_token');
      const res = await fetch('/api/v1/employee/my-transfer-order/pdf', {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Download failed');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Transfer_Order_${(transferOrder?.order_number || 'order').replace(/\//g, '-')}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Transfer Order downloaded');
    } catch (err) {
      toast.error('Failed to download transfer order');
    } finally {
      setOrderDownloading(false);
    }
  };

  return (
    <div style={{ minHeight: '100vh', background: 'linear-gradient(135deg, #1a237e 0%, #283593 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20, fontFamily: 'inherit' }}>
      <div style={{ background: '#fff', borderRadius: 16, padding: 40, maxWidth: 560, width: '100%', textAlign: 'center', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
        <div style={{ fontSize: 64, marginBottom: 16 }}>✅</div>
        <h2 style={{ fontSize: 22, fontWeight: 800, color: '#1a237e', marginBottom: 8 }}>Application Submitted!</h2>
        <p style={{ fontSize: 14, color: '#555', marginBottom: 20, lineHeight: 1.6 }}>
          Your transfer application has been submitted successfully and is under review.
        </p>

        {application?.ref_number && (
          <div style={{ background: '#e8eaf6', borderRadius: 10, padding: '12px 20px', marginBottom: 20, display: 'inline-block' }}>
            <div style={{ fontSize: 11, color: '#666', marginBottom: 2 }}>Reference Number</div>
            <div style={{ fontSize: 18, fontWeight: 800, color: '#1a237e', letterSpacing: 1 }}>{application.ref_number}</div>
          </div>
        )}

        <div style={{ background: '#f5f5f5', borderRadius: 10, padding: 16, marginBottom: 24, textAlign: 'left' }}>
          <div style={{ fontSize: 12, color: '#666', marginBottom: 8, fontWeight: 700 }}>Application Summary</div>
          {[
            ['Employee ID', form.employee_id_display || '—'],
            ['Name', form.employee_name || '—'],
            ['Department', form.dept_name || '—'],
            ['Designation', form.name_of_post || '—'],
            ['Status', 'Submitted ✅'],
          ].map(([l, v]) => (
            <div key={l} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, padding: '4px 0', borderBottom: '1px solid #eee' }}>
              <span style={{ color: '#888' }}>{l}</span>
              <span style={{ color: '#333', fontWeight: 500 }}>{v}</span>
            </div>
          ))}
        </div>

        <div style={{ display: 'flex', gap: 12, flexDirection: 'column' }}>
          <button
            onClick={handleDownloadPDF}
            disabled={pdfLoading}
            style={{ width: '100%', padding: '12px 0', background: '#1a237e', color: '#fff', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
            {pdfLoading ? '⏳ Generating PDF...' : '📄 Download Acknowledgement Receipt'}
          </button>
          {transferOrder && (
            <button
              onClick={downloadTransferOrder}
              disabled={orderDownloading}
              style={{ width: '100%', padding: '12px 0', background: '#2e7d32', color: '#fff', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
              {orderDownloading ? '⏳ Downloading...' : '📄 Download Transfer Order'}
            </button>
          )}
          <button
            onClick={onLogout}
            style={{ width: '100%', padding: '10px 0', background: '#f5f5f5', color: '#555', border: '1px solid #e0e0e0', borderRadius: 8, fontSize: 13, cursor: 'pointer', fontFamily: 'inherit' }}>
            ← Back to Login
          </button>
        </div>
      </div>
    </div>
  );
}


// ── Application Window Closed Screen ─────────────────────
function ClosedScreen() {
  return (
    <div style={{ minHeight: '100vh', background: '#f5f5f5', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24, fontFamily: 'inherit' }}>
      <div style={{ maxWidth: 520, width: '100%', textAlign: 'center' }}>
        <div style={{ width: 80, height: 80, borderRadius: '50%', background: '#ffebee', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 24px' }}>
          <span style={{ fontSize: 36 }}>🔒</span>
        </div>
        <div style={{ background: '#fff', border: '1px solid #e0e0e0', borderRadius: 12, padding: '28px 28px 22px' }}>
          <div style={{ display: 'inline-block', background: '#ffebee', color: '#c62828', fontSize: 12, padding: '4px 14px', borderRadius: 8, marginBottom: 16, fontWeight: 700 }}>
            Submission window closed
          </div>
          <h1 style={{ fontSize: 20, fontWeight: 700, color: '#1a237e', margin: '0 0 10px' }}>Application Submission Closed</h1>
          <p style={{ fontSize: 15, color: '#555', lineHeight: 1.7, margin: '0 0 20px' }}>
            The online transfer application window has been closed.<br />No further submissions will be accepted.
          </p>
          <div style={{ background: '#f5f5f5', borderRadius: 8, padding: '12px 16px', marginBottom: 20 }}>
            <div style={{ fontSize: 13, color: '#666', marginBottom: 4 }}>Window closed at</div>
            <div style={{ fontSize: 22, fontWeight: 700, color: '#1a237e' }}>8:00 PM, 19-05-2026</div>
          </div>
          <div style={{ borderTop: '1px solid #e0e0e0', paddingTop: 16 }}>
            <p style={{ fontSize: 13, color: '#999', margin: '0 0 8px', lineHeight: 1.6 }}>
              For queries, contact the MA Department (CDMA / DTCP)
            </p>
            <div style={{ fontSize: 13, color: '#666' }}>
              General Transfers 2026 — Government of Telangana
            </div>
          </div>
        </div>
        <p style={{ fontSize: 12, color: '#bbb', marginTop: 12 }}>
          You have not submitted an application. The submission window is now closed.
        </p>
        <button
          onClick={() => { localStorage.removeItem('tg_token'); localStorage.removeItem('tg_user'); window.location.reload(); }}
          style={{ marginTop: 12, background: 'none', border: '1px solid #e0e0e0', borderRadius: 8, padding: '8px 24px', fontSize: 13, color: '#999', cursor: 'pointer', fontFamily: 'inherit' }}>
          ← Logout
        </button>
      </div>
    </div>
  );
}


function EmployeePortal({ onLogout }) {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('section1');
  const [application, setApplication] = useState(null);
  const [masters, setMasters] = useState({ multiZones: [], zones: [], districts: [], allDistricts: [], ulbs: [], cadres: [] });
  const [form, setForm] = useState({});
  // Initialise selected_dept_id from user context when portal loads
  useEffect(() => {
    if (user?.deptId) setField('selected_dept_id', String(user.deptId));
  }, [user?.deptId]); // eslint-disable-line react-hooks/exhaustive-deps
  const [saving, setSaving] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [preferences, setPreferences] = useState([{ priority: 1, ulbId: '', orgId: '', reason: '' }]);
  const [declarationChecked, setDeclarationChecked] = useState(false);
  const [uploading, setUploading] = useState({});
  const [availableULBs, setAvailableULBs] = useState([]);
  const [appLoaded, setAppLoaded] = useState(false);
  const [mastersLoaded, setMastersLoaded] = useState(false);
  const [portalModal, setPortalModal] = useState(null);
  // Track uploaded doc s3 keys in state so validators can check them
  const [uploadedDocs, setUploadedDocs] = useState({});
  const uploadedDocsRef = useRef({});
  // Ref to always have latest form without stale closure
  const formRef = useRef({});
  formRef.current = form;

  const tabs = [
    { key: 'section1', label: 'Section I', subtitle: 'Employee Info' },
    { key: 'section2', label: 'Section II', subtitle: 'Present Post' },
    { key: 'section3', label: 'Section III–IX', subtitle: 'Special Categories' },
    { key: 'preferences', label: 'Section X', subtitle: 'Preferences' },
    { key: 'upload', label: 'Upload & Submit', subtitle: 'Signed Copy' },
    { key: 'orders', label: 'Section XI', subtitle: 'Orders' },
  ];

  // Load masters once
  useEffect(() => {
    const loadMasters = async () => {
      try {
        const [mzRes, allDistRes, cadresRes] = await Promise.all([
          mastersAPI.getMultiZones(),
          mastersAPI.getAllDistricts(),
          mastersAPI.getAllCadres(String(user?.deptId) || '1'), // derived from DB via login
        ]);
        setMasters(m => ({ ...m, multiZones: mzRes.data, allDistricts: allDistRes.data, cadres: cadresRes.data })); setMastersLoaded(true);
        setMastersLoaded(true);
      } catch { toast.error('Failed to load master data'); }
    };
    loadMasters();
  }, [user?.deptId]); // reload when dept changes (from DB)

  // Load existing application ONCE on mount — never overwrite form after that
  useEffect(() => {
    const loadApp = async () => {
      try {
        const res = await applicationsAPI.getMyApplication();
        if (res.data) {
          setApplication(res.data);
          // Populate form from saved data
          const saved = res.data;
          // Resolve post_cadre_type from cadre_id via DB cadres data
          // This ensures Post Cadre radio is correctly set on reload
          const resolvedCadreType = saved.post_cadre_type || null; // DB stores it via buildPayload
          setForm({
            ...saved,
            selected_dept_id: String(user?.deptId) || saved.selected_dept_id || null,
            post_cadre_type: resolvedCadreType,
          });
          if (saved.preferences?.length)
            setPreferences(saved.preferences.map(p => ({
              priority: p.priority,
              ulbId: p.org_id ? '' : String(p.ulb_id || ''),
              orgId: p.org_id ? String(p.org_id) : '',
              reason: p.reason || '',
            })));
          if (saved.declaration_accepted) setDeclarationChecked(true);
          if (saved.status === 'submitted') setSubmitted(true);
          // Reload cascading dropdowns WITHOUT clearing form
          if (saved.multi_zone_id) {
            const zonesRes = await mastersAPI.getZones(saved.multi_zone_id);
            setMasters(m => ({ ...m, zones: zonesRes.data }));
          }
          if (saved.zone_id) {
            const distRes = await mastersAPI.getDistricts(saved.zone_id);
            setMasters(m => ({ ...m, districts: distRes.data }));
          }
          if (saved.district_id) {
            const ulbRes = await mastersAPI.getULBs(saved.district_id);
            setMasters(m => ({ ...m, ulbs: ulbRes.data }));
            setAvailableULBs(ulbRes.data);
          }
        }
      } catch { /* no existing app is fine */ }
      finally { setAppLoaded(true); }
    };
    loadApp();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Cascading loaders — do NOT clear form, only update dropdown options
  const loadZones = async (mzId) => {
    if (!mzId) return;
    const res = await mastersAPI.getZones(mzId);
    setMasters(m => ({ ...m, zones: res.data }));
  };

  const loadDistricts = async (zoneId) => {
    if (!zoneId) return;
    const res = await mastersAPI.getDistricts(zoneId);
    setMasters(m => ({ ...m, districts: res.data }));
  };

  const loadULBs = async (districtId) => {
    if (!districtId) return;
    const res = await mastersAPI.getULBs(districtId);
    setMasters(m => ({ ...m, ulbs: res.data }));
    setAvailableULBs(res.data);
  };

  // Load available ULBs/Orgs for Section X preferences
  // DTCP: loads from organizations filtered by name_of_post + cadre_category
  // MA/EnC(PH): loads from ulbs filtered by cadre type (State/Multi-Zone/Zone/District)
  useEffect(() => {
    if (!appLoaded) return;
    const isDTCP = String(user?.deptId) === '3';

    // DTCP — trigger when cadre or cadre_category changes
    if (isDTCP) {
      if (!form.cadre_id) return; // need at least designation selected
      const loadDTCPOrgs = async () => {
        try {
          // Get name_of_post from loaded cadres
          const selectedCadre = masters.cadres.find(c => String(c.id) === String(form.cadre_id));
          if (!selectedCadre) return;
          const res = await mastersAPI.getAllOrgs(
            '3',
            selectedCadre.name_of_post,
            form.cadre_category || undefined
          );
          const orgs = (res.data || []).map(o => ({
            id: String(o.id),
            name: o.org_name,
            district_name: o.cadre_category,
            is_org: true,
          }));
          setAvailableULBs(orgs);
        } catch (e) {
          console.warn('[Preferences DTCP] load error:', e.message);
        }
      };
      loadDTCPOrgs();
      return;
    }

    // Non-DTCP — needs post_cadre_type
    if (!form.post_cadre_type) return;
    const loadPrefsULBs = async () => {
      try {
        let res;
        const cadre = form.post_cadre_type;
        if (cadre === 'State') {
          res = await mastersAPI.getAllULBs();
        } else if (cadre === 'Multi-Zone') {
          if (!form.multi_zone_id) return;
          res = await mastersAPI.getULBsByMultiZone(form.multi_zone_id);
        } else if (cadre === 'Zone') {
          if (!form.zone_id) return;
          res = await mastersAPI.getULBsByZone(form.zone_id);
        } else if (cadre === 'District') {
          if (!form.district_id) return;
          res = await mastersAPI.getULBs(form.district_id);
        } else {
          return;
        }
        const seen = new Set();
        // Check if Category-III cadre is selected
        const _selCadre = masters.cadres?.find(c => String(c.id) === String(form.cadre_id));
        const _isCat3 = _selCadre?.name_of_post?.includes('Category-III') ||
                        _selCadre?.name_of_post?.includes('Category III');
        const CAT3_PREFIXES = ['Manager -', 'Revenue Officer -', 'Superintendent -'];
        const isCat3ULB = (u) => CAT3_PREFIXES.some(p => u.name?.startsWith(p));

        const unique = (res.data || []).filter(u => {
          if (seen.has(u.id)) return false;
          seen.add(u.id);
          // Other cadres: HIDE Manager/Revenue Officer/Superintendent ULBs
          if (!_isCat3) return !isCat3ULB(u);
          // Category-III: show ALL ULBs (zone-based) including Manager/RO/Superintendent
          return true;
        });
        setAvailableULBs(unique);
      } catch (e) {
        console.warn('[Preferences] load error:', e.message);
      }
    };
    loadPrefsULBs();
  }, [form.selected_dept_id, form.cadre_id, form.cadre_category, form.post_cadre_type, form.multi_zone_id, form.zone_id, form.district_id, appLoaded, masters.cadres]); // eslint-disable-line react-hooks/exhaustive-deps

  // setField: always merge into existing form, never replace
  const setField = useCallback((key, val) => {
    setForm(f => ({ ...f, [key]: val }));
  }, []);

  const buildPayload = useCallback((currentForm, currentDeclaration) => ({
    employeeName: currentForm.employee_name || '',
    dateOfBirth: currentForm.date_of_birth || null,
    gender: currentForm.gender || null,
    postCadreType: currentForm.post_cadre_type || null,
    cadreId: currentForm.cadre_id || null,
    multiZoneId: currentForm.multi_zone_id || null,
    zoneId: currentForm.zone_id || null,
    districtId: currentForm.district_id || null,
    localCadreUlbId: currentForm.local_cadre_ulb_id || null,
    dateOfJoining: currentForm.date_of_joining || null,
    dateOfRetirement: currentForm.date_of_retirement || null,
    nativeDistrictId: currentForm.native_district_id || null,
    mobileNumber: currentForm.mobile_number || null,
    selectedDeptId: String(user?.deptId) || currentForm.selected_dept_id || null,
    orgId: currentForm.org_id || null,
    cadreCategory: currentForm.cadre_category || null,
    presentOfficeName: currentForm.present_office_name || null,
    presentPostName: currentForm.present_post_name || null,
    dateWorkingPresentStation: currentForm.date_working_present_station || null,
    presentStationPeriodYears: currentForm.present_station_period_years || 0,
    presentStationPeriodMonths: currentForm.present_station_period_months || 0,
    presentStationPeriodDays: currentForm.present_station_period_days || 0,
    spouseEmployed: currentForm.spouse_employed || false,
    spouseEmployerType: currentForm.spouse_employer_type || null,
    spouseName: currentForm.spouse_name || null,
    spouseDept: currentForm.spouse_dept || null,
    spousePost: currentForm.spouse_post || null,
    spouseHq: currentForm.spouse_hq || null,
    hasDisability: currentForm.has_disability || false,
    hasMentallyRetardedChild: currentForm.has_mentally_retarded_child || false,
    isWidow: currentForm.is_widow || false,
    hasMedicalGrounds: currentForm.has_medical_grounds || false,
    medicalGroundsPerson: currentForm.medical_grounds_person || null,
    medicalDiseaseCategory: currentForm.medical_disease_category || null,
    disciplinaryCaseDetails: currentForm.disciplinary_case_details || null,
    hasDisciplinaryCase: currentForm.has_disciplinary_case || false,
    hasCensusDuty: currentForm.has_census_duty || false,
    declarationAccepted: currentDeclaration || false,
    declarationDate: currentDeclaration ? new Date().toISOString().split('T')[0] : null,
    preferences: preferences.filter(p => p.ulbId || p.orgId).map(p => ({
      priority: p.priority,
      ulbId: p.orgId ? null : p.ulbId,
      orgId: p.orgId || null,
      reason: p.reason,
    })),
  }), [preferences]);

  // Keep declarationChecked in a ref so handleSave always has latest value
  const declarationRef = useRef(declarationChecked);
  useEffect(() => { declarationRef.current = declarationChecked; }, [declarationChecked]);

  // handleSave — fire-and-forget background save, never blocks UI
  const handleSave = useCallback(async (silent = false) => {
    setSaving(true);
    try {
      const payload = buildPayload(formRef.current, declarationRef.current);
      const res = await applicationsAPI.saveApplication(payload);
      if (res.data?.id) {
        // Reload full application to get fresh presigned URLs for documents
        try {
          const fullApp = await applicationsAPI.getMyApplication();
          if (fullApp.data) setApplication(fullApp.data);
          else setApplication(prev => ({ ...(prev || {}), id: res.data.id }));
        } catch {
          setApplication(prev => ({ ...(prev || {}), id: res.data.id }));
        }
      }
      if (!silent) toast.success('Progress saved');
      return true;
    } catch (err) {
      if (!silent) toast.error(err.response?.data?.error || 'Save failed');
      return false;
    } finally { setSaving(false); }
  }, [buildPayload]);

  // Navigate with optional validation — shows modal if required fields missing
  const goToTab = useCallback((tab, validate) => {
    if (validate) {
      const err = validate();
      if (err) {
        // Parse "⚠ Section X: field1, field2" into title + lines
        const parts = err.replace('⚠ ', '').split(': ');
        const title = parts[0] || 'Required Fields Missing';
        const fields = parts[1] ? parts[1].split(', ') : [];
        setPortalModal({
          type: 'warn',
          title,
          message: 'Please complete the following required fields before proceeding:',
          lines: fields,
          confirmLabel: 'Go Back & Fill',
        });
        return;
      }
    }
    setActiveTab(tab);
    handleSave(true).catch(() => {});
  }, [handleSave, setPortalModal]);

  // Auto-save every 60 seconds
  useEffect(() => {
    if (!appLoaded) return;
    const timer = setInterval(() => handleSave(true), 60000);
    return () => clearInterval(timer);
  }, [appLoaded, handleSave]);

  // Keep application id in ref so submit always has latest
  const applicationRef = useRef(null);
  useEffect(() => { applicationRef.current = application; }, [application]);

  const handleSubmit = async () => {
    if (!declarationRef.current) { toast.error('Please accept the declaration'); return; }
    setSaving(true);
    try {
      const payload = buildPayload(formRef.current, declarationRef.current);
      const saveRes = await applicationsAPI.saveApplication(payload);
      if (!saveRes.data?.id) throw new Error('Save failed — no application ID returned');
      const appId = applicationRef.current?.id || saveRes.data.id;
      setApplication(prev => ({ ...(prev || {}), id: appId }));
      const res = await applicationsAPI.submitApplication(appId);
      const refNum = res.data.refNumber || res.data.ref_number || '';
      setApplication(prev => ({ ...(prev || {}), id: appId, ref_number: refNum }));
      applicationRef.current = { ...(applicationRef.current || {}), id: appId, ref_number: refNum };
      setSubmitted(true);
      toast.success('Application submitted! Ref: ' + refNum);
    } catch (err) {
      const errData = err.response?.data;
      if (errData?.fields) toast.error('Missing required fields: ' + errData.fields.join(', '));
      else toast.error(errData?.error || err.message || 'Submission failed');
    } finally { setSaving(false); }
  };

  const handleFileUpload = async (docType, file) => {
    // Auto-save to get an ID if we don't have one
    let appId = applicationRef.current?.id;
    if (!appId) {
      try {
        const payload = buildPayload(formRef.current, declarationRef.current);
        const saveRes = await applicationsAPI.saveApplication(payload);
        appId = saveRes.data.id;
        // Reload application to get fresh presigned URL for uploaded document
        try {
          const fullApp = await applicationsAPI.getMyApplication();
          if (fullApp.data) setApplication(fullApp.data);
          else setApplication(prev => ({ ...(prev || {}), id: appId }));
        } catch {
          setApplication(prev => ({ ...(prev || {}), id: appId }));
        }
        applicationRef.current = { ...(applicationRef.current || {}), id: appId };
      } catch (e) {
        toast.error('Please save your application before uploading documents');
        return;
      }
    }
    setUploading(u => ({ ...u, [docType]: true }));
    try {
      const uploadRes = await applicationsAPI.uploadDocument(appId, docType, file);
      // Map docType to the s3_key field name so validators can check it
      const keyMap = {
        sr_entry_doc:           'sr_entry_doc_s3_key',
        disability_cert:        'disability_cert_s3_key',
        mentally_retarded_cert: 'mentally_retarded_cert_s3_key',
        medical_cert:           'medical_cert_s3_key',
        census_order:           'census_order_s3_key',
        spouse_proof:           'spouse_proof_s3_key',
        signed_copy:            'signed_copy_s3_key',
      };
      const s3Field = keyMap[docType];
      if (s3Field) {
        // Update both form state and uploadedDocs ref so validators see the upload
        const s3Key = uploadRes?.data?.s3Key || uploadRes?.data?.key || `uploaded/${docType}/${appId}`;
        setField(s3Field, s3Key);
        setUploadedDocs(prev => {
          const next = { ...prev, [s3Field]: s3Key };
          uploadedDocsRef.current = next;
          return next;
        });
      }
      toast.success('Document uploaded successfully');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Upload failed');
    } finally { setUploading(u => ({ ...u, [docType]: false })); }
  };

  if (submitted || (isDeadlinePassed() && application?.status && application.status !== 'draft')) {
    return <SubmittedScreen application={application} form={form} onLogout={onLogout} deadlinePassed={isDeadlinePassed()} />;
  }
  if (!mastersLoaded) return <LoadingScreen message='Loading form data...' />;

  return (
    <div className="employee-portal">
      {/* Unified header — single teal band, no white boxes */}
      <header className="app-header">
        {/* Top strip: brand + save/logout */}
        <div className="header-top">
          <div className="header-brand">
            <img src="/assets/telangana-logo.png" alt="GoT" className="nav-logo-tg" />
            <div className="header-brand-text">
              <span className="brand-main">General Transfers 2026</span>
              <span className="brand-sub">Government of Telangana · MA Department</span>
            </div>
          </div>
          <div className="header-actions">
            <span className="header-user">👤 {user?.employeeId}</span>
            <button className="btn-nav" onClick={() => handleSave(false)} disabled={saving}>{saving ? '⏳' : '💾'} Save</button>
            <button className="btn-nav danger" onClick={onLogout}>↩ Logout</button>
          </div>
        </div>

        {/* Banner: logos + title */}
        <div className="header-banner">
          <div className="banner-logo">
            <img src="/assets/telangana-logo.png" alt="Government of Telangana" className="banner-logo-tg" />
          </div>
          <div className="banner-title">
            <h1>ANNEXURE-II — GENERAL TRANSFERS 2026</h1>
            <p>G.O.Ms.No.38, Finance (HRM-I) Department, Dated: 21.04.2026 · Employee Option Form</p>
          </div>
          <div className="banner-logo">
            <img src="/assets/rising-logo.png" alt="Telangana Rising" className="banner-logo-rising" />
          </div>
        </div>

        {/* Status strip — inside header, no layout shift */}
        {application?.ref_number && (
          <div className="status-strip">
            <span>📋 Ref: <strong>{application.ref_number}</strong></span>
            <span className={`badge ${application.status}`}>{application.status?.toUpperCase()}</span>
          </div>
        )}
      </header>

      {/* Tab Bar */}
      <div className="tab-bar">
        {tabs.map((tab, i) => (
          <button key={tab.key} className={`tab-btn ${activeTab === tab.key ? 'active' : ''}`} onClick={() => setActiveTab(tab.key)}>
            <span className="step-badge">{i + 1}</span>
            <span style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <span>{tab.label}</span>
              <span style={{ fontSize: 9.5, fontWeight: 400, opacity: .7 }}>{tab.subtitle}</span>
            </span>
          </button>
        ))}
      </div>

      {/* Section content — fixed container prevents layout jump */}
      <div className="section-content">

      {/* Section I */}
      {activeTab === 'section1' && (
        <Section1
          form={form} setField={setField}
          masters={masters}
          onZoneChange={loadZones}
          onDistrictChange={loadDistricts}
          onULBChange={loadULBs}
          onSave={() => handleSave(false)}
          saving={saving}
          isDTCP={String(user?.deptId) === '3'}
          onNext={() => goToTab('section2', () => {
              const f = formRef.current;
              const isDTCP = String(user?.deptId) === '3';
              const missing = [];
              if (!f.employee_name?.trim()) missing.push('Employee Name');
              if (!f.date_of_birth) missing.push('Date of Birth');
              if (!f.gender) missing.push('Gender');
              if (!f.date_of_joining) missing.push('Date of Initial Joining');
              if (!f.date_of_retirement) missing.push('Date of Retirement');
              if (!f.native_district_id) missing.push('Native District');
              if (!f.mobile_number || f.mobile_number.length < 10) missing.push('Mobile Number (10 digits)');
              if (isDTCP) {
                // DTCP: Post Cadre + cadre_id (designation) + cadre_category + org_id
                if (!f.post_cadre_type) missing.push('Post Cadre');
                if (!f.cadre_id)        missing.push('Designation');
                if (!f.cadre_category)  missing.push('Cadre Category');
                if (!f.org_id)          missing.push('ULB / Organisation');
              } else {
                if (!f.post_cadre_type) missing.push('Post Cadre');
                if (!f.cadre_id) missing.push('Name of Post (Designation)');
                if (!f.multi_zone_id) missing.push('Multi Zone');
                if (!f.zone_id) missing.push('Zone');
                if (!f.district_id) missing.push('District');
                // Category-III cadre — ULB not required in Section I (shown only in preferences)
              const _cat = masters?.cadres?.find(c => String(c.id) === String(f.cadre_id));
              const _isCat3 = _cat?.name_of_post?.includes('Category-III') || _cat?.name_of_post?.includes('Category III');
              if (!_isCat3 && !f.local_cadre_ulb_id) missing.push('Name of ULB (Salary Drawing ULB)');
              }
              if (missing.length) return '⚠ Please fill required fields in Section I: ' + missing.join(', ');
              return null;
            })}
        />
      )}

      {/* Section II */}
      {activeTab === 'section2' && (
        <Section2
          form={form} setField={setField}
          masters={masters}
          application={application}
          uploading={uploading}
          onFileUpload={handleFileUpload}
          onSave={() => handleSave(false)}
          saving={saving}
          onBack={() => setActiveTab('section1')}
          onNext={() => goToTab('section3', () => {
              const f = formRef.current;
              const missing = [];
              if (!f.present_office_name?.trim()) missing.push('Name of ULB (Salary Drawing ULB)');
              if (!f.present_post_name?.trim()) missing.push('Name of Present Post');
              if (!f.date_working_present_station) missing.push('Reported Date of Present Working Station');
              // SR Entry is optional — not required to proceed
              if (missing.length) return '⚠ Please fill required fields in Section II: ' + missing.join(', ');
              return null;
            })}
        />
      )}

      {/* Section III–IX */}
      {activeTab === 'section3' && (
        <Section3to9
          form={form} setField={setField}
          application={application}
          uploading={uploading}
          onFileUpload={handleFileUpload}
          onSave={() => handleSave(false)}
          saving={saving}
          onBack={() => setActiveTab('section2')}
          onNext={() => goToTab('preferences', () => {
              const f = formRef.current;
              if (f.spouse_employed && !f.spouse_proof_s3_key)
                return '⚠ Section III: Please upload Spouse Employment Proof Document.';
              if (f.has_disability && !f.disability_cert_s3_key)
                return '⚠ Section IV: Please upload Disability Certificate.';
              if (f.has_mentally_retarded_child && !f.mentally_retarded_cert_s3_key)
                return '⚠ Section V: Please upload Mentally Retarded Child Certificate.';
              if (f.has_medical_grounds && !f.medical_cert_s3_key)
                return '⚠ Section VII: Please upload Medical Certificate.';
              if (f.has_census_duty && !f.census_order_s3_key)
                return '⚠ Section IX: Please upload Census Appointment Order.';
              return null;
            })}
        />
      )}

      {/* Section X — Preferences */}
      {activeTab === 'preferences' && (
        <SectionPreferences
          form={form}
          preferences={preferences}
          setPreferences={setPreferences}
          availableULBs={availableULBs}
          declarationChecked={declarationChecked}
          setDeclarationChecked={setDeclarationChecked}
          saving={saving}
          onSave={handleSave}
          onSubmit={handleSubmit}
          onBack={() => setActiveTab('section3')}
          onNext={() => goToTab('upload', () => {
              if (!declarationChecked)
                return '⚠ Please accept the Self-Declaration before proceeding.';
              return null;
            })}
          application={application}
          masters={masters}
        />
      )}

      {/* Upload & Submit */}
      {activeTab === 'upload' && (
        <SectionUploadSubmit
          form={form}
          application={application}
          uploading={uploading}
          onFileUpload={handleFileUpload}
          saving={saving}
          onSave={() => handleSave(false)}
          onSubmit={handleSubmit}
          onBack={() => setActiveTab('preferences')}
        />
      )}

      {/* Section XI — Orders */}
      {activeTab === 'orders' && (
        <SectionOrders onBack={() => setActiveTab('upload')} />
      )}

      </div>{/* /section-content */}

      {/* Validation / info modals */}
      <AppModal modal={portalModal} onClose={() => setPortalModal(null)} />
    </div>
  );
}

// ── Section I Component ────────────────────────────────────
function Section1({ form, setField, masters, onZoneChange, onDistrictChange, onULBChange, onNext, onSave, saving, isDTCP }) {
  const today = new Date().toISOString().split('T')[0];
  const { user } = useAuth();
  const [appModal, setAppModal] = useState(null);

  // Post Cadre radio drives designation dropdown filter
  const handleCadreTypeChange = (type) => {
    setField('post_cadre_type', type);
    setField('cadre_id', '');
  };

  // Designation auto-sets cadre_type
  const handleCadreChange = (cadreId) => {
    setField('cadre_id', cadreId);
    const cadre = masters.cadres.find(c => String(c.id) === String(cadreId));
    if (cadre) {
      setField('post_cadre_type', cadre.cadre_type);
      setField('present_post_name', cadre.name_of_post); // Auto-fill Section II
    }
  };

  // Age validation — reusable for both onChange and load-time check
  const calcAge = (val) => {
    if (!val) return null;
    const dob = new Date(val);
    const today = new Date();
    return today.getFullYear() - dob.getFullYear() -
      (today < new Date(today.getFullYear(), dob.getMonth(), dob.getDate()) ? 1 : 0);
  };

  const showAgeModal = (val, age) => {
    setAppModal({
      type: 'block',
      title: 'Not Eligible for Transfer',
      message: 'Employees above 61 years of age are not eligible to apply for General Transfers 2026.',
      lines: [
        `Date of Birth: ${new Date(val).toLocaleDateString('en-IN')}`,
        `Calculated Age: ${age} years`,
        'Please contact the MA Department for further assistance.',
      ],
      confirmLabel: 'I Understand',
    });
  };

  const handleDOBChange = (val) => {
    if (!val) { setField('date_of_birth', ''); return; }
    const age = calcAge(val);
    if (age > 61) {
      // Clear the invalid DOB — do NOT save it
      setField('date_of_birth', '');
      showAgeModal(val, age);
    } else {
      setField('date_of_birth', val);
    }
  };

  // ── Field-level validators — run on change AND on load ──────
  // Mobile: exactly 10 digits
  const validateMobile = (val) => {
    if (!val) return null;
    const digits = val.replace(/\D/g, '');
    if (digits.length > 0 && digits.length < 10)
      return 'Mobile number must be exactly 10 digits.';
    return null;
  };

  // Date of Joining: must not be in future
  const validateJoining = (val) => {
    if (!val) return null;
    const today = new Date().toISOString().split('T')[0];
    const joinDate = typeof val === 'string' ? val.split('T')[0] : new Date(val).toISOString().split('T')[0];
    if (joinDate > today)
      return 'Date of Initial Joining cannot be a future date.';
    return null;
  };

  // Date of Retirement: must be in future
  const validateRetirement = (val) => {
    if (!val) return null;
    // Compare date-only strings to avoid timezone shift issues
    const today = new Date().toISOString().split('T')[0];
    const retireDate = typeof val === 'string' ? val.split('T')[0] : new Date(val).toISOString().split('T')[0];
    if (retireDate <= today)
      return 'Date of Retirement must be a future date.';
    return null;
  };

  // Cross-check joining vs retirement
  const validateJoinRetire = (joining, retirement) => {
    if (!joining || !retirement) return null;
    const j = typeof joining === 'string' ? joining.split('T')[0] : new Date(joining).toISOString().split('T')[0];
    const r = typeof retirement === 'string' ? retirement.split('T')[0] : new Date(retirement).toISOString().split('T')[0];
    if (j >= r)
      return 'Date of Initial Joining must be before Date of Retirement.';
    return null;
  };

  // Date of Working Station: must not be after cutoff 31/12/2025
  const validateWorkingDate = (val) => {
    if (!val) return null;
    if (new Date(val) > new Date('2025-12-31'))
      return 'Reported Date of Present Working Station cannot be after 31/12/2025.';
    if (new Date(val) > new Date())
      return 'Reported Date of Present Working Station cannot be a future date.';
    return null;
  };

  // Show a field-level warning modal (non-blocking warn type)
  const showFieldWarning = (title, message) => {
    setAppModal({ type: 'warn', title, message, confirmLabel: 'OK' });
  };

  // ── On-change handlers with validation ────────────────────
  const handleJoiningChange = (val) => {
    const err = validateJoining(val) || validateJoinRetire(val, form.date_of_retirement);
    if (err) { showFieldWarning('Invalid Date of Initial Joining', err); setField('date_of_joining', ''); }
    else setField('date_of_joining', val);
  };

  const handleRetirementChange = (val) => {
    const err = validateRetirement(val) || validateJoinRetire(form.date_of_joining, val);
    if (err) { showFieldWarning('Invalid Date of Retirement', err); setField('date_of_retirement', ''); }
    else setField('date_of_retirement', val);
  };

  const handleWorkingDateChange = (val) => {
    const err = validateWorkingDate(val);
    if (err) { showFieldWarning('Invalid Reported Date', err); setField('date_working_present_station', ''); }
    else {
      setField('date_working_present_station', val);
      if (val) {
        const p = calcServicePeriod(val);
        if (p) {
          setField('present_station_period_years', p.years);
          setField('present_station_period_months', p.months);
          setField('present_station_period_days', p.days);
        }
      }
    }
  };

  const handleMobileChange = (val) => {
    const digits = val.replace(/\D/g, '').slice(0, 10);
    setField('mobile_number', digits);
    // No modal for mobile — inline error shown in UI
  };

  // ── On-load validation — triggers when form fields are populated ──
  const [loadChecked, setLoadChecked] = useState(false);
  useEffect(() => {
    if (loadChecked) return;
    if (!form.date_of_birth && !form.date_of_joining && !form.date_of_retirement) return;
    setLoadChecked(true);

    // DOB age check
    if (form.date_of_birth) {
      const age = calcAge(form.date_of_birth);
      if (age !== null && age > 61) {
        showAgeModal(form.date_of_birth, age);
        setField('date_of_birth', '');
        return;
      }
    }

    // Joining date check
    const joinErr = validateJoining(form.date_of_joining) ||
                    validateJoinRetire(form.date_of_joining, form.date_of_retirement);
    if (joinErr) {
      // Just warn — do not clear the saved value
      console.warn('Joining date warning on load:', joinErr);
    }

    // Retirement date check — only warn, never clear on load
    // (Date was valid when saved; don't clear it on reload)
    const retireErr = validateRetirement(form.date_of_retirement);
    if (retireErr) {
      // Just warn — do not clear the saved value
      console.warn('Retirement date warning on load:', retireErr);
    }

  }, [form.date_of_birth, form.date_of_joining, form.date_of_retirement]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleMultiZoneChange = (mzId) => {
    setField('multi_zone_id', mzId);
    setField('zone_id', '');
    setField('district_id', '');
    setField('local_cadre_ulb_id', '');
    if (mzId) onZoneChange(mzId);
  };

  const handleZoneChange = (zoneId) => {
    setField('zone_id', zoneId);
    setField('district_id', '');
    setField('local_cadre_ulb_id', '');
    if (zoneId) onDistrictChange(zoneId);
  };

  const handleDistrictChange = (districtId) => {
    setField('district_id', districtId);
    setField('local_cadre_ulb_id', '');
    if (districtId) onULBChange(districtId);
  };

  // Filter designations by selected cadre type — memoized for performance
  const filteredCadres = React.useMemo(
    () => form.post_cadre_type
      ? masters.cadres.filter(c => c.cadre_type === form.post_cadre_type)
      : masters.cadres,
    [form.post_cadre_type, masters.cadres]
  );

  // Find selected ULB name for display
  const selectedULB = masters.ulbs.find(u => String(u.id) === String(form.local_cadre_ulb_id));

  return (
    <div className="page-container">

      {/* ── Subsection A: Personal Information ─────────────── */}
      <div className="card">
        <div className="card-header">
          <div className="section-icon">👤</div>
          <div>
            <h2 style={{ marginBottom: 2 }}>SECTION I — Employee Information</h2>
            <p style={{ fontSize: 11.5, color: 'var(--text-muted)', fontWeight: 500, margin: 0 }}>
              Subsection A — Personal Details
            </p>
          </div>
        </div>
        <div className="form-grid">

          <div className="form-group">
            <label>a. Employee ID</label>
            <input type="text" value={user?.employeeId || form.employee_id_display || ''} disabled style={{ background: '#f5f5f5' }} />
          </div>

          <div className="form-group">
            <label>b. Name of Employee <span className="required">*</span></label>
            <input type="text" value={form.employee_name || ''} onChange={e => setField('employee_name', e.target.value)} placeholder="Full name as per service records" />
          </div>

          <div className="form-group">
            <label>c. Date of Birth <span className="required">*</span></label>
            <input type="date" value={toDateStr(form.date_of_birth)} onChange={e => handleDOBChange(e.target.value)} max={today} />
          </div>

          <div className="form-group">
            <label>d. Gender <span className="required">*</span></label>
            <select value={form.gender || ''} onChange={e => setField('gender', e.target.value)}>
              <option value="">— Select —</option>
              <option>Male</option><option>Female</option><option>Other</option>
            </select>
          </div>

          <div className="form-group">
            <label>e. Mobile Number <span className="required">*</span></label>
            <div style={{ display: 'flex', gap: 0 }}>
              <span style={{
                display: 'flex', alignItems: 'center', padding: '9px 12px',
                background: '#f0faf9', border: '1.5px solid var(--border)',
                borderRight: 'none', borderRadius: '8px 0 0 8px',
                fontSize: 14, fontWeight: 700, color: 'var(--teal-dark)',
                flexShrink: 0, letterSpacing: .5,
              }}>+91</span>
              <input
                type="tel" inputMode="numeric"
                value={form.mobile_number || ''}
                onChange={e => handleMobileChange(e.target.value)}
                placeholder="10-digit number" maxLength={10}
                style={{ borderRadius: '0 8px 8px 0', flex: 1 }}
              />
            </div>
            {form.mobile_number && form.mobile_number.length > 0 && form.mobile_number.length < 10 && (
              <span className="field-error">Enter a valid 10-digit mobile number</span>
            )}
            {form.mobile_number && form.mobile_number.length === 10 && (
              <span style={{ fontSize: 11, color: 'var(--success)', marginTop: 3, display: 'block' }}>✅ +91 {form.mobile_number}</span>
            )}
          </div>

          <div className="form-group">
            <label>f. Date of Initial Joining <span className="required">*</span></label>
            <input type="date" value={toDateStr(form.date_of_joining)} onChange={e => handleJoiningChange(e.target.value)} max={today} />
          </div>

          <div className="form-group">
            <label>g. Date of Retirement <span className="required">*</span></label>
            <input type="date" value={toDateStr(form.date_of_retirement)} onChange={e => handleRetirementChange(e.target.value)} min={today} />
          </div>

          <div className="form-group">
            <label>h. Native District <span className="required">*</span></label>
            <select value={form.native_district_id || ''} onChange={e => setField('native_district_id', e.target.value)}>
              <option value="">— Select District —</option>
              {masters.allDistricts.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
            </select>
          </div>

          {/* i. Post Cadre — for DTCP only Multi-Zone and Zone are relevant */}
          <div className="form-group span2">
            <label>i. Post Cadre <span className="required">*</span></label>
            <div className="radio-group">
              {/* Derive available cadre types from DB — only show types that have cadres for this dept */}
              {[...new Set((masters.cadres || []).map(c => c.cadre_type))]
                .sort((a, b) => {
                  const order = ['State', 'Multi-Zone', 'Zone', 'District'];
                  return order.indexOf(a) - order.indexOf(b);
                })
                .map(type => (
                  <label key={type} className={`radio-option ${form.post_cadre_type === type ? 'selected' : ''}`}
                    onClick={() => handleCadreTypeChange(type)}>
                    <input type="radio" name="post_cadre_type" value={type} readOnly checked={form.post_cadre_type === type} /> {type}
                  </label>
                ))
              }
            </div>
          </div>

          {/* j. Designation — filtered from cadres by dept_id + cadre_type */}
          <div className="form-group span2">
            <label>j. Name of Post (Designation) <span className="required">*</span></label>
            <select value={form.cadre_id || ''} onChange={e => handleCadreChange(e.target.value)}>
              <option value="">— {form.post_cadre_type ? `Select ${form.post_cadre_type} Designation` : 'Select Post Cadre first'} —</option>
              {filteredCadres.map(c => <option key={c.id} value={c.id}>{c.name_of_post}</option>)}
            </select>
            {form.post_cadre_type && filteredCadres.length === 0 && (
              <span style={{ fontSize: 12, color: '#c62828' }}>No designations found for {form.post_cadre_type} cadre</span>
            )}
          </div>

        </div>
      </div>

      {/* ── Subsection B: Present Working Station — hidden for DTCP ── */}
      {!isDTCP && (
        <div className="card" style={{ borderTop: '3px solid var(--teal-mid)' }}>
          <div className="card-header">
            <div className="section-icon">🏢</div>
            <div>
              <h2 style={{ marginBottom: 2 }}>SECTION I — Employee Information</h2>
              <p style={{ fontSize: 11.5, color: 'var(--text-muted)', fontWeight: 500, margin: 0 }}>
                Subsection B — Present Working Station Information
              </p>
            </div>
          </div>
          <p style={{ fontSize: 12.5, color: 'var(--text-muted)', marginBottom: 18, padding: '8px 12px', background: 'var(--teal-pale)', borderRadius: 6, borderLeft: '3px solid var(--teal-mid)' }}>
            This information is used to identify the employee's present working station for transfer processing.
          </p>
          <div className="form-grid">
            <div className="form-group">
              <label>i. Multi Zone <span className="required">*</span></label>
              <select value={form.multi_zone_id || ''} onChange={e => handleMultiZoneChange(e.target.value)}>
                <option value="">— Select Multi Zone —</option>
                {masters.multiZones.map(mz => <option key={mz.id} value={mz.id}>{mz.name}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label>j. Zone <span className="required">*</span></label>
              <select value={form.zone_id || ''} onChange={e => handleZoneChange(e.target.value)} disabled={!form.multi_zone_id}>
                <option value="">— Select Zone —</option>
                {masters.zones.map(z => <option key={z.id} value={z.id}>{z.name}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label>k. District <span className="required">*</span></label>
              <select value={form.district_id || ''} onChange={e => handleDistrictChange(e.target.value)} disabled={!form.zone_id}>
                <option value="">— Select District —</option>
                {masters.districts.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label>l. Name of ULB (Salary Drawing ULB) <span className="required">*</span></label>
              <select value={form.local_cadre_ulb_id || ''} onChange={e => {
                const ulbId = e.target.value;
                setField('local_cadre_ulb_id', ulbId);
                // Auto-fill Section II — Name of ULB (Salary Drawing ULB)
                const ulb = masters.ulbs.find(u => String(u.id) === String(ulbId));
                if (ulb) setField('present_office_name', ulb.name);
                else if (!ulbId) setField('present_office_name', '');
              }} disabled={!form.district_id}>
                <option value="">— Select ULB —</option>
                {masters.ulbs.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
              </select>
            </div>
          </div>
        </div>
      )}

      {/* ── DTCP Subsection B: Cadre Category + ULB/Organisation ── */}
      {isDTCP && (
        <DTCPSubsectionB form={form} setField={setField} masters={masters} />
      )}

      <div className="action-bar">
        <div />
        <div style={{ display: 'flex', gap: 12 }}>
          <button className="btn btn-secondary" onClick={onSave} disabled={saving}>
            {saving ? '⏳' : '💾'} Save
          </button>
          <button className="btn btn-primary" onClick={onNext}>
            Next: Present Post →
          </button>
        </div>
      </div>
    </div>
  );
}



// ── Admin Panel ───────────────────────────────────────────
function AdminPanel({ onLogout }) {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = React.useState('overview');
  const [stats, setStats] = React.useState({});
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    adminAPI.getStats()
      .then(r => setStats(r.data || {}))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const tabs = [
    { key: 'overview',        label: '📊 Overview' },
    { key: 'applications',    label: '📋 All Applications' },
    { key: 'transfer_orders', label: '📨 Release Transfer Orders' },
    { key: 'view_transfer_orders', label: '📋 View Transfer Orders' },
    { key: 'users',           label: '👥 Employee Accounts' },
    { key: 'zones',           label: '🗺 Multi Zones & Zones' },
    { key: 'districts',       label: '📍 Districts' },
    { key: 'ulbs',            label: '🏙 Urban Local Bodies' },
    { key: 'cadres',          label: '🎖 Post Cadres' },
    { key: 'orgs',            label: '🏢 Organizations (DTCP)' },
  ];

  return (
    <div style={{ minHeight: '100vh', background: '#f5f5f5', fontFamily: 'inherit' }}>
      {/* Header */}
      <div style={{ background: '#1a237e', color: '#fff', padding: '12px 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <div style={{ fontSize: 16, fontWeight: 800 }}>General Transfers 2026 — Admin Panel</div>
          <div style={{ fontSize: 12, opacity: 0.75 }}>Government of Telangana · MA Department</div>
        </div>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          <span style={{ background: '#e53935', fontSize: 11, padding: '2px 10px', borderRadius: 10, fontWeight: 700 }}>ADMIN</span>
          <span style={{ fontSize: 13 }}>{user?.employeeId}</span>
          <button onClick={onLogout} style={{ background: 'rgba(255,255,255,.15)', border: 'none', color: '#fff', padding: '6px 14px', borderRadius: 6, cursor: 'pointer', fontSize: 12 }}>← Logout</button>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '220px 1fr', minHeight: 'calc(100vh - 52px)' }}>
        {/* Sidebar */}
        <div style={{ background: '#fff', borderRight: '1px solid #e0e0e0', padding: '16px 0' }}>
          <div style={{ padding: '0 16px', fontSize: 10, color: '#9e9e9e', fontWeight: 700, letterSpacing: 1, marginBottom: 8, textTransform: 'uppercase' }}>Dashboard</div>
          {['overview','applications'].map(key => { const t = tabs.find(x=>x.key===key); return t ? (
            <button key={t.key} onClick={() => setActiveTab(t.key)} style={{ width: '100%', textAlign: 'left', padding: '10px 16px', border: 'none', background: activeTab === t.key ? '#e8eaf6' : 'none', color: activeTab === t.key ? '#1a237e' : '#333', fontWeight: activeTab === t.key ? 700 : 400, cursor: 'pointer', fontSize: 13, borderLeft: activeTab === t.key ? '3px solid #1a237e' : '3px solid transparent', fontFamily: 'inherit' }}>{t.label}</button>
          ) : null; })}
          <div style={{ padding: '12px 16px 4px', fontSize: 10, color: '#9e9e9e', fontWeight: 700, letterSpacing: 1, marginTop: 8, textTransform: 'uppercase' }}>Transfer Orders</div>
          <button onClick={() => setActiveTab('transfer_orders')} style={{ width: '100%', textAlign: 'left', padding: '10px 16px', border: 'none', background: activeTab === 'transfer_orders' ? '#e8eaf6' : 'none', color: activeTab === 'transfer_orders' ? '#1a237e' : '#333', fontWeight: activeTab === 'transfer_orders' ? 700 : 400, cursor: 'pointer', fontSize: 13, borderLeft: activeTab === 'transfer_orders' ? '3px solid #1a237e' : '3px solid transparent', fontFamily: 'inherit' }}>📨 Release Transfer Orders</button>
          <button onClick={() => setActiveTab('view_transfer_orders')} style={{ width: '100%', textAlign: 'left', padding: '10px 16px', border: 'none', background: activeTab === 'view_transfer_orders' ? '#e8eaf6' : 'none', color: activeTab === 'view_transfer_orders' ? '#1a237e' : '#333', fontWeight: activeTab === 'view_transfer_orders' ? 700 : 400, cursor: 'pointer', fontSize: 13, borderLeft: activeTab === 'view_transfer_orders' ? '3px solid #1a237e' : '3px solid transparent', fontFamily: 'inherit' }}>📋 View Transfer Orders</button>
          <div style={{ padding: '12px 16px 4px', fontSize: 10, color: '#9e9e9e', fontWeight: 700, letterSpacing: 1, marginTop: 8, textTransform: 'uppercase' }}>Accounts</div>
          <button onClick={() => setActiveTab('users')} style={{ width: '100%', textAlign: 'left', padding: '10px 16px', border: 'none', background: activeTab === 'users' ? '#e8eaf6' : 'none', color: activeTab === 'users' ? '#1a237e' : '#333', fontWeight: activeTab === 'users' ? 700 : 400, cursor: 'pointer', fontSize: 13, borderLeft: activeTab === 'users' ? '3px solid #1a237e' : '3px solid transparent', fontFamily: 'inherit' }}>👥 Employee Accounts</button>
          <div style={{ padding: '12px 16px 4px', fontSize: 10, color: '#9e9e9e', fontWeight: 700, letterSpacing: 1, marginTop: 8, textTransform: 'uppercase' }}>Masters</div>
          {tabs.filter(t => ['zones','districts','ulbs','cadres','orgs'].includes(t.key)).map(t => (
            <button key={t.key} onClick={() => setActiveTab(t.key)} style={{ width: '100%', textAlign: 'left', padding: '10px 16px', border: 'none', background: activeTab === t.key ? '#e8eaf6' : 'none', color: activeTab === t.key ? '#1a237e' : '#333', fontWeight: activeTab === t.key ? 700 : 400, cursor: 'pointer', fontSize: 13, borderLeft: activeTab === t.key ? '3px solid #1a237e' : '3px solid transparent', fontFamily: 'inherit' }}>{t.label}</button>
          ))}
        </div>

        {/* Main Content */}
        <div style={{ padding: 24, overflowY: 'auto' }}>
          {activeTab === 'overview' && (
            <div>
              <h2 style={{ fontSize: 18, fontWeight: 800, color: '#1a237e', marginBottom: 20 }}>Dashboard Overview</h2>
              {loading ? <div style={{ color: '#9e9e9e' }}>Loading stats...</div> : (
                <div>
                  {/* Stats cards */}
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 24 }}>
                    {[
                      { label: 'Total Applications', value: stats.total || 0, color: '#1a237e', bg: '#e8eaf6' },
                      { label: 'Submitted', value: stats.submitted || 0, color: '#2e7d32', bg: '#e8f5e9' },
                      { label: 'Draft', value: stats.draft || 0, color: '#e65100', bg: '#fff3e0' },
                      { label: 'Total Employees', value: stats.employees || 0, color: '#6a1b9a', bg: '#f3e5f5' },
                    ].map(s => (
                      <div key={s.label} style={{ background: s.bg, borderRadius: 10, padding: 16 }}>
                        <div style={{ fontSize: 11, color: s.color, marginBottom: 4 }}>{s.label}</div>
                        <div style={{ fontSize: 28, fontWeight: 800, color: s.color }}>{s.value}</div>
                      </div>
                    ))}
                  </div>

                  {/* Status breakdown */}
                  {(stats.byStatus || []).length > 0 && (
                    <div style={{ background: '#fff', border: '1px solid #e0e0e0', borderRadius: 10, padding: 16, marginBottom: 16 }}>
                      <div style={{ fontSize: 14, fontWeight: 700, color: '#1a237e', marginBottom: 12 }}>Applications by Status</div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
                        {(stats.byStatus || []).map(s => (
                          <div key={s.status} style={{ background: '#f5f5f5', borderRadius: 8, padding: '8px 14px', fontSize: 13 }}>
                            <span style={{ fontWeight: 700 }}>{s.count}</span> <span style={{ color: '#666' }}>{s.status}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Dept breakdown */}
                  {(stats.byDept || []).length > 0 && (
                    <div style={{ background: '#fff', border: '1px solid #e0e0e0', borderRadius: 10, padding: 16 }}>
                      <div style={{ fontSize: 14, fontWeight: 700, color: '#1a237e', marginBottom: 12 }}>Applications by Department</div>
                      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                        {(stats.byDept || []).map(d => (
                          <div key={d.dept_name} style={{ background: '#e8eaf6', borderRadius: 8, padding: '12px 20px', fontSize: 13 }}>
                            <div style={{ fontWeight: 800, fontSize: 22, color: '#1a237e' }}>{d.total}</div>
                            <div style={{ color: '#555', marginTop: 2 }}>{d.dept_name || d.dept_code || 'Unknown'}</div>
                            <div style={{ fontSize: 11, color: '#9e9e9e' }}>{d.submitted} submitted</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
          {activeTab === 'applications' && <AdminApplications />}
          {activeTab === 'transfer_orders' && <ReleaseTransferOrders />}
          {activeTab === 'view_transfer_orders' && <ViewTransferOrders />}
          {activeTab === 'users' && <AdminUsers />}
          {activeTab === 'zones' && <MasterZones />}
          {activeTab === 'districts' && <MasterDistricts />}
          {activeTab === 'ulbs' && <MasterULBs />}
          {activeTab === 'cadres' && <MasterCadres />}
          {activeTab === 'orgs' && <MasterOrganizations />}
        </div>
      </div>
    </div>
  );
}

// ── Release Transfer Orders ───────────────────────────────
function ReleaseTransferOrders() {
  const [step, setStep] = React.useState('upload'); // upload | preview | generating | done
  const [rows, setRows] = React.useState([]);
  const [fileName, setFileName] = React.useState('');
  const [generating, setGenerating] = React.useState(false);
  const [results, setResults] = React.useState([]);
  const [error, setError] = React.useState('');

  const COLUMNS = [
    { key: 'emp_id',              label: 'EmpId' },
    { key: 'full_name',           label: 'Full Name' },
    { key: 'mobile_no',           label: 'Mobile No' },
    { key: 'present_designation', label: 'Present Designation' },
    { key: 'present_ulb',         label: 'Present Working ULB' },
    { key: 'multi_zone',          label: 'Multi-Zone' },
    { key: 'transfer_designation',label: 'Transfer Designation' },
    { key: 'approving_authority', label: 'Approving Authority' },
    { key: 'transferred_ulb',     label: 'Transferred ULB' },
  ];

  const EXPECTED_HEADERS = ['EmpId','FULL NAME','Mobile No.','Present Designation',
    'Present Working ULB(salary Drawing ULB)','Multi-Zone',
    'Transfer Designation','Approving Authority','Transferred ULB'];

  const handleFile = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setError('');
    setFileName(file.name);
    try {
      const XLSX = await import('xlsx');
      const data = await file.arrayBuffer();
      const wb = XLSX.read(data);
      const ws = wb.Sheets[wb.SheetNames[0]];
      const json = XLSX.utils.sheet_to_json(ws, { defval: '' });
      if (!json.length) { setError('Excel file is empty'); return; }

      // Map columns flexibly
      const mapped = json.map((row, i) => {
        const keys = Object.keys(row);
        return {
          _row: i + 2,
          emp_id:               String(row[keys.find(k => k.toLowerCase().includes('empid') || k.toLowerCase() === 'empid') || keys[0]] || '').trim(),
          full_name:            String(row[keys.find(k => k.toLowerCase().includes('full') || k.toLowerCase().includes('name')) || keys[1]] || '').trim(),
          mobile_no:            String(row[keys.find(k => k.toLowerCase().includes('mobile')) || keys[2]] || '').trim(),
          present_designation:  String(row[keys.find(k => k.toLowerCase().includes('present desig') || k.toLowerCase().includes('present design')) || keys[3]] || '').trim(),
          present_ulb:          String(row[keys.find(k => k.toLowerCase().includes('present working') || k.toLowerCase().includes('salary')) || keys[4]] || '').trim(),
          multi_zone:           String(row[keys.find(k => k.toLowerCase().includes('multi')) || keys[5]] || '').trim(),
          transfer_designation: String(row[keys.find(k => k.toLowerCase().includes('transfer desig') || k.toLowerCase().includes('transfer design')) || keys[6]] || '').trim(),
          approving_authority:  String(row[keys.find(k => k.toLowerCase().includes('approving') || k.toLowerCase().includes('authority')) || keys[7]] || '').trim(),
          transferred_ulb:      String(row[keys.find(k => k.toLowerCase().includes('transferred ulb') || (k.toLowerCase().includes('transfer') && k.toLowerCase().includes('ulb'))) || keys[8]] || '').trim(),
          _status: 'pending',
          _error: '',
        };
      }).filter(r => r.emp_id);

      if (!mapped.length) { setError('No valid rows found. Ensure EmpId column exists.'); return; }
      setRows(mapped);
      setStep('preview');
    } catch (err) {
      setError('Failed to read Excel: ' + err.message);
    }
    e.target.value = '';
  };

  const handleGenerate = async () => {
    setGenerating(true);
    setStep('generating');
    const token = localStorage.getItem('tg_token');
    const updated = [...rows];
    for (let i = 0; i < updated.length; i++) {
      try {
        const res = await fetch('/api/v1/transfer-orders', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify(updated[i]),
        });
        const data = await res.json();
        if (res.ok) {
          updated[i] = { ...updated[i], _status: 'success', _order_number: data.order_number };
        } else {
          updated[i] = { ...updated[i], _status: 'error', _error: data.error || 'Failed' };
        }
      } catch (err) {
        updated[i] = { ...updated[i], _status: 'error', _error: err.message };
      }
      setRows([...updated]);
    }
    setGenerating(false);
    setStep('done');
  };

  const btn = (label, onClick, color='#1a237e', disabled=false) => (
    <button onClick={onClick} disabled={disabled} style={{
      background: disabled ? '#bdbdbd' : color, color: '#fff', border: 'none',
      borderRadius: 8, padding: '10px 24px', fontSize: 13, fontWeight: 700,
      cursor: disabled ? 'not-allowed' : 'pointer', fontFamily: 'inherit'
    }}>{label}</button>
  );

  const inputStyle = { display: 'none' };

  return (
    <div>
      <h2 style={{ fontSize: 18, fontWeight: 800, color: '#1a237e', marginBottom: 4 }}>📨 Release Transfer Orders</h2>
      <p style={{ fontSize: 13, color: '#666', marginBottom: 20 }}>Upload Excel → Preview → Generate Proceeding Orders & Send via WhatsApp</p>

      {/* Step Indicator */}
      <div style={{ display: 'flex', gap: 0, marginBottom: 24 }}>
        {[['1', 'Upload Excel', 'upload'], ['2', 'Preview & Verify', 'preview'], ['3', 'Generate & Release', 'done']].map(([num, label, key], i) => {
          const isActive = step === key || (key === 'preview' && step === 'generating') || (key === 'done' && step === 'done');
          const isDone = (key === 'upload' && ['preview','generating','done'].includes(step)) ||
                         (key === 'preview' && ['generating','done'].includes(step));
          const stepColor = isDone ? '#2e7d32' : isActive ? '#1a237e' : '#9e9e9e';
          return (
            <div key={key} style={{ display: 'flex', alignItems: 'center', flex: 1 }}>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1 }}>
                <div style={{ width: 32, height: 32, borderRadius: '50%', background: stepColor, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, fontWeight: 700, marginBottom: 4 }}>
                  {isDone ? '✓' : num}
                </div>
                <div style={{ fontSize: 11, color: stepColor, fontWeight: isActive ? 700 : 400, textAlign: 'center' }}>{label}</div>
              </div>
              {i < 2 && <div style={{ flex: 0.5, height: 2, background: isDone ? '#2e7d32' : '#e0e0e0', margin: '0 4px', marginBottom: 18 }} />}
            </div>
          );
        })}
      </div>

      {/* STEP 1 — Upload */}
      {step === 'upload' && (
        <div style={{ background: '#fff', border: '1px solid #e0e0e0', borderRadius: 12, padding: 32, textAlign: 'center' }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>📊</div>
          <h3 style={{ fontSize: 16, fontWeight: 700, color: '#1a237e', marginBottom: 8 }}>Upload Transfer Orders Excel</h3>
          <p style={{ fontSize: 13, color: '#666', marginBottom: 20, lineHeight: 1.6 }}>
            Excel must contain columns:<br />
            <strong>EmpId, Full Name, Mobile No., Present Designation, Present Working ULB, Multi-Zone, Transfer Designation, Approving Authority, Transferred ULB</strong>
          </p>
          {error && <div style={{ background: '#ffebee', color: '#c62828', borderRadius: 8, padding: '10px 16px', marginBottom: 16, fontSize: 13 }}>⚠ {error}</div>}
          <label style={{ background: '#1a237e', color: '#fff', borderRadius: 8, padding: '12px 32px', fontSize: 14, fontWeight: 700, cursor: 'pointer', display: 'inline-block' }}>
            <input type="file" accept=".xlsx,.xls,.csv" onChange={handleFile} style={inputStyle} />
            📂 Choose Excel File
          </label>
          <div style={{ marginTop: 12, fontSize: 12, color: '#999' }}>Supports .xlsx, .xls, .csv</div>
        </div>
      )}

      {/* STEP 2 — Preview */}
      {(step === 'preview' || step === 'generating' || step === 'done') && (
        <div>
          {/* Summary bar */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 12, marginBottom: 16 }}>
            {[
              ['Total', rows.length, '#1a237e', '#e8eaf6'],
              ['Pending', rows.filter(r=>r._status==='pending').length, '#e65100', '#fff3e0'],
              ['Success', rows.filter(r=>r._status==='success').length, '#2e7d32', '#e8f5e9'],
              ['Failed', rows.filter(r=>r._status==='error').length, '#c62828', '#ffebee'],
            ].map(([l,v,c,bg]) => (
              <div key={l} style={{ background: bg, borderRadius: 8, padding: '10px 14px', textAlign: 'center' }}>
                <div style={{ fontSize: 22, fontWeight: 800, color: c }}>{v}</div>
                <div style={{ fontSize: 11, color: c }}>{l}</div>
              </div>
            ))}
          </div>

          {/* File info + actions */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <div style={{ fontSize: 13, color: '#555' }}>
              📄 <strong>{fileName}</strong> — {rows.length} employees loaded
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              {step === 'preview' && (
                <>
                  <label style={{ background: '#fff', border: '1px solid #e0e0e0', color: '#555', borderRadius: 8, padding: '8px 16px', fontSize: 12, cursor: 'pointer', fontWeight: 600 }}>
                    <input type="file" accept=".xlsx,.xls,.csv" onChange={handleFile} style={inputStyle} />
                    🔄 Re-upload
                  </label>
                  {btn('🚀 Generate & Release Orders', handleGenerate, '#1a237e')}
                </>
              )}
              {step === 'generating' && btn('⏳ Generating...', null, '#9e9e9e', true)}
              {step === 'done' && (
                <>
                  {btn('📥 Download Results', () => {
                    const headers = ['EmpId','Full Name','Mobile','Status','Order Number','Error'];
                    const escape2 = v => '"' + String(v||'').replace(/"/g,'""') + '"';
                    const csvData = [['EmpId','Full Name','Mobile','Status','Order Number','Error'].join(','), ...rows.map(r =>
                      [r.emp_id, r.full_name, r.mobile_no, r._status, r._order_number||'', r._error||''].map(escape2).join(','))];
                    const csvStr = csvData.join('\n');
                    const a = document.createElement('a');
                    a.href = URL.createObjectURL(new Blob([csvStr], {type:'text/csv'}));
                    a.download = `transfer_orders_results_${new Date().toISOString().split('T')[0]}.csv`;
                    a.click();
                  }, '#2e7d32')}
                  {btn('+ New Batch', () => { setStep('upload'); setRows([]); setFileName(''); setError(''); }, '#455a64')}
                </>
              )}
            </div>
          </div>

          {/* Table */}
          <div style={{ background: '#fff', border: '1px solid #e0e0e0', borderRadius: 10, overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr style={{ background: '#f5f5f5' }}>
                  <th style={{ padding: '8px 10px', textAlign: 'center', fontWeight: 600, fontSize: 11, color: '#666', borderBottom: '1px solid #e0e0e0', whiteSpace: 'nowrap', width: 40 }}>#</th>
                  {COLUMNS.map(c => (
                    <th key={c.key} style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 600, fontSize: 11, color: '#666', borderBottom: '1px solid #e0e0e0', whiteSpace: 'nowrap' }}>{c.label}</th>
                  ))}
                  <th style={{ padding: '8px 10px', textAlign: 'center', fontWeight: 600, fontSize: 11, color: '#666', borderBottom: '1px solid #e0e0e0', width: 120 }}>Status</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, i) => {
                  const bg = row._status === 'success' ? '#f1f8f1' : row._status === 'error' ? '#fff5f5' : (i%2===0?'#fff':'#fafafa');
                  return (
                    <tr key={i} style={{ background: bg }}>
                      <td style={{ padding: '7px 10px', textAlign: 'center', color: '#999', borderBottom: '1px solid #f0f0f0', fontSize: 11 }}>{i+1}</td>
                      {COLUMNS.map(c => (
                        <td key={c.key} style={{ padding: '7px 10px', borderBottom: '1px solid #f0f0f0', maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontWeight: c.key==='emp_id'?700:400, color: c.key==='emp_id'?'#1a237e':'#333' }} title={row[c.key]}>
                          {row[c.key] || <span style={{color:'#e0e0e0'}}>—</span>}
                        </td>
                      ))}
                      <td style={{ padding: '7px 10px', textAlign: 'center', borderBottom: '1px solid #f0f0f0' }}>
                        {row._status === 'pending' && <span style={{ color: '#9e9e9e', fontSize: 11 }}>⏳ Pending</span>}
                        {row._status === 'success' && <span style={{ background: '#e8f5e9', color: '#2e7d32', padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 700 }}>✅ {row._order_number || 'Done'}</span>}
                        {row._status === 'error' && <span style={{ background: '#ffebee', color: '#c62828', padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 700 }} title={row._error}>❌ Failed</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {step === 'generating' && (
            <div style={{ background: '#e3f2fd', border: '1px solid #90caf9', borderRadius: 8, padding: '12px 16px', marginTop: 14, fontSize: 13, color: '#1565c0', fontWeight: 600 }}>
              ⏳ Generating transfer orders... {rows.filter(r=>r._status==='success').length + rows.filter(r=>r._status==='error').length} of {rows.length} processed
            </div>
          )}

          {step === 'done' && (
            <div style={{ background: '#e8f5e9', border: '1px solid #a5d6a7', borderRadius: 8, padding: '12px 16px', marginTop: 14, fontSize: 13, color: '#2e7d32', fontWeight: 600 }}>
              ✅ Complete — {rows.filter(r=>r._status==='success').length} orders generated, {rows.filter(r=>r._status==='error').length} failed
            </div>
          )}
        </div>
      )}
    </div>
  );
}


// ── Shared Applications Table View ──────────────────────
function ApplicationsTableView({ deptFilter, cadreFilter: cadreFilterProp, districtFilter }) {
  const [data, setData] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [search, setSearch] = React.useState('');
  const [docsModal, setDocsModal] = React.useState(null);
  const [docsLoading, setDocsLoading] = React.useState(false);
  const [statusFilter, setStatusFilter] = React.useState('');
  const [cadreFilter, setCadreFilter] = React.useState('');
  const [desigFilter, setDesigFilter] = React.useState('');
  const [activeDept, setActiveDept] = React.useState(deptFilter || 'ALL');
  const [chk, setChk] = React.useState({ spouse: false, census: false, disability: false, medical: false });

  React.useEffect(() => {
    applicationsAPI.getAllApplications({ limit: 500, page: 1 })
      .then(r => setData(r.data?.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const viewDocs = async (appId, empName) => {
    setDocsModal({ appId, empName, docs: null });
    setDocsLoading(true);
    try {
      const res = await applicationsAPI.getDocuments(appId);
      setDocsModal({ appId, empName, docs: res.data?.documents || [] });
    } catch {
      toast.error('Failed to load documents');
      setDocsModal(null);
    } finally { setDocsLoading(false); }
  };

  const depts = deptFilter ? [deptFilter] : ['ALL', 'CDMA', 'DTCP'];

  const desigs = React.useMemo(() => {
    const base = data.filter(r => activeDept === 'ALL' || r.dept_name === activeDept);
    return [...new Set(base.map(r => r.name_of_post).filter(Boolean))].sort();
  }, [data, activeDept]);

  const filtered = React.useMemo(() => {
    const s = search.toLowerCase();
    return data.filter(r =>
      (activeDept === 'ALL' || r.dept_name === activeDept) &&
      (!s || (r.employee_id_display + (r.employee_name || '')).toLowerCase().includes(s)) &&
      (!statusFilter || r.status === statusFilter) &&
      (!(cadreFilter || cadreFilterProp) || r.post_cadre_type === (cadreFilter || cadreFilterProp)) &&
      (!desigFilter || r.name_of_post === desigFilter) &&
      (!districtFilter || String(r.district_id) === String(districtFilter)) &&
      (!chk.spouse || r.spouse_employed) &&
      (!chk.census || r.has_census_duty) &&
      (!chk.disability || r.has_disability) &&
      (!chk.medical || r.has_medical_grounds)
    );
  }, [data, activeDept, search, statusFilter, cadreFilter, cadreFilterProp, desigFilter, districtFilter, chk]);

  const stats = React.useMemo(() => {
    const base = data.filter(r => activeDept === 'ALL' || r.dept_name === activeDept);
    return {
      total: base.length,
      submitted: base.filter(r => r.status === 'submitted').length,
      draft: base.filter(r => r.status === 'draft').length,
      spouse: base.filter(r => r.spouse_employed).length,
    };
  }, [data, activeDept]);

  const resetFilters = () => {
    setSearch(''); setStatusFilter(''); setCadreFilter(''); setDesigFilter('');
    setChk({ spouse: false, census: false, disability: false, medical: false });
  };

  const downloadCSV = () => {
    const rows = filtered;
    const fmtDate = (d) => {
      if (!d) return '';
      try {
        // Parse YYYY-MM-DD directly — never use new Date() to avoid timezone shift
        const str = String(d).split('T')[0]; // handles both '1987-01-15' and ISO strings
        const [yyyy, mm, dd] = str.split('-');
        if (!yyyy || !mm || !dd) return str;
        return `${dd}/${mm}/${yyyy}`;
      } catch { return String(d); }
    };
    const yn = v => v ? 'Yes' : 'No';
    const escape = v => `"${String(v == null ? '' : v).replace(/"/g, '""')}"`;
    const getPref = (r, n) => {
      const p = (r.preferences || []).find(x => x.priority === n);
      return p ? (p.ulb_name || p.org_name || '') : '';
    };
    const getPrefReason = (r, n) => {
      const p = (r.preferences || []).find(x => x.priority === n);
      return p ? (p.reason || '') : '';
    };
    const period = r => `${r.present_station_period_years || 0}y ${r.present_station_period_months || 0}m ${r.present_station_period_days || 0}d`;

    const headers = [
      'Employee ID', 'Name', 'Gender', 'Mobile', 'Department', 'Cadre Type',
      'Designation', 'Date of Birth', 'Date of Initial Joining', 'Date of Retirement',
      'Multi Zone', 'Zone', 'District', 'Native District',
      'Salary Drawing ULB / Organisation', 'Present Post Name', 'Status',
      'Reported Date of Present Working Station', 'Station Period',
      'Spouse Employed', 'Spouse Name', 'Spouse Employer Type', 'Spouse Department', 'Spouse HQ', 'Spouse Post',
      'Has Disability', 'Has MR Child', 'Is Widow/Widower',
      'Has Medical Grounds', 'Medical Grounds Person', 'Medical Disease Category',
      'Has Census Duty', 'Has Disciplinary Case', 'Disciplinary Case Details',
      'Pref 1', 'Pref 1 Reason', 'Pref 2', 'Pref 2 Reason',
      'Pref 3', 'Pref 3 Reason', 'Pref 4', 'Pref 4 Reason',
      'Pref 5', 'Pref 5 Reason',
    ];

    const csv = [headers.join(','), ...rows.map(r => [
      r.employee_id_display, r.employee_name, r.gender || '', r.mobile_number,
      r.dept_name, r.post_cadre_type, r.name_of_post,
      fmtDate(r.date_of_birth), fmtDate(r.date_of_joining), fmtDate(r.date_of_retirement),
      r.multi_zone_name || '', r.zone_name || '', r.district_name || '', r.native_district_name || '',
      r.present_office_name || '', r.present_post_name || '', r.status,
      fmtDate(r.date_working_present_station), period(r),
      yn(r.spouse_employed), r.spouse_name || '', r.spouse_employer_type || '',
      r.spouse_dept || '', r.spouse_hq || '', r.spouse_post || '',
      yn(r.has_disability), yn(r.has_mentally_retarded_child), yn(r.is_widow),
      yn(r.has_medical_grounds), r.medical_grounds_person || '', r.medical_disease_category || '',
      yn(r.has_census_duty), yn(r.has_disciplinary_case), r.disciplinary_case_details || '',
      getPref(r,1), getPrefReason(r,1), getPref(r,2), getPrefReason(r,2),
      getPref(r,3), getPrefReason(r,3), getPref(r,4), getPrefReason(r,4),
      getPref(r,5), getPrefReason(r,5),
    ].map(escape).join(','))].join('\n');

    const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `applications_${activeDept}_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  const chip = v => v
    ? <span style={{ background: '#e8f5e9', color: '#1b5e20', padding: '1px 7px', borderRadius: 10, fontSize: 11 }}>Yes</span>
    : <span style={{ color: '#bbb', fontSize: 11 }}>No</span>;

  const statusBadge = s => {
    const map = { submitted: ['#e8f5e9','#1b5e20'], draft: ['#fff3e0','#e65100'], queried: ['#e3f2fd','#0c447c'], resubmitted: ['#f3e5f5','#6a1b9a'] };
    const [bg, color] = map[s] || ['#f5f5f5','#555'];
    return <span style={{ background: bg, color, padding: '2px 8px', borderRadius: 20, fontSize: 11, fontWeight: 600 }}>{s}</span>;
  };

  const inputStyle = { height: 30, border: '1px solid #e0e0e0', borderRadius: 6, padding: '0 10px', fontSize: 12, fontFamily: 'inherit', background: '#fff' };
  const chkLabelStyle = (active) => ({ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, cursor: 'pointer', padding: '4px 10px', border: `1px solid ${active ? '#1a237e' : '#e0e0e0'}`, borderRadius: 6, background: active ? '#e8eaf6' : '#fff', color: active ? '#1a237e' : '#555', userSelect: 'none' });

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: '#9e9e9e' }}>Loading applications...</div>;

  return (
    <div>
      {/* Documents Modal */}
      {docsModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: 20 }}>
          <div style={{ background: '#fff', borderRadius: 12, padding: 24, maxWidth: 560, width: '100%', maxHeight: '80vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <div>
                <div style={{ fontSize: 15, fontWeight: 700, color: '#1a237e' }}>Documents</div>
                <div style={{ fontSize: 12, color: '#666' }}>{docsModal.empName}</div>
              </div>
              <button onClick={() => setDocsModal(null)} style={{ background: '#f5f5f5', border: 'none', borderRadius: 6, padding: '6px 12px', cursor: 'pointer', fontSize: 13 }}>✕ Close</button>
            </div>
            {docsLoading ? (
              <div style={{ textAlign: 'center', padding: 24, color: '#999' }}>Loading documents...</div>
            ) : docsModal.docs?.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 24, color: '#bbb' }}>No documents uploaded</div>
            ) : (
              <div>
                {(docsModal.docs || []).map((doc, i) => (
                  <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 12px', background: '#f8f8f8', borderRadius: 8, marginBottom: 8 }}>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 600, color: '#333' }}>{doc.label}</div>
                      <div style={{ fontSize: 11, color: '#999' }}>{doc.filename || 'Document'}</div>
                    </div>
                    {doc.url ? (
                      <a href={doc.url} target="_blank" rel="noopener noreferrer" style={{ background: '#1a237e', color: '#fff', padding: '6px 14px', borderRadius: 6, fontSize: 12, fontWeight: 600, textDecoration: 'none' }}>
                        👁 View
                      </a>
                    ) : (
                      <span style={{ fontSize: 11, color: '#bbb' }}>Not available</span>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Dept tabs — only show if not restricted */}
      {!deptFilter && (
        <div style={{ display: 'flex', gap: 6, marginBottom: 14, flexWrap: 'wrap' }}>
          {depts.map(d => (
            <button key={d} onClick={() => setActiveDept(d)} style={{ padding: '6px 16px', border: `1px solid ${activeDept === d ? '#1a237e' : '#e0e0e0'}`, borderRadius: 20, fontSize: 12, cursor: 'pointer', background: activeDept === d ? '#1a237e' : '#fff', color: activeDept === d ? '#fff' : '#555', fontWeight: activeDept === d ? 700 : 400, fontFamily: 'inherit' }}>{d === 'ALL' ? 'All departments' : d}</button>
          ))}
        </div>
      )}

      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 8, marginBottom: 14 }}>
        {[['Total', stats.total, '#1a237e', '#e8eaf6'], ['Submitted', stats.submitted, '#1b5e20', '#e8f5e9'], ['Draft', stats.draft, '#e65100', '#fff3e0'], ['Spouse category', stats.spouse, '#0c447c', '#e3f2fd']].map(([l, v, c, bg]) => (
          <div key={l} style={{ background: bg, borderRadius: 8, padding: '10px 14px', textAlign: 'center' }}>
            <div style={{ fontSize: 22, fontWeight: 700, color: c }}>{v}</div>
            <div style={{ fontSize: 11, color: c, opacity: 0.8, marginTop: 2 }}>{l}</div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div style={{ background: '#fff', border: '1px solid #e0e0e0', borderRadius: 10, padding: '12px 16px', marginBottom: 12 }}>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginBottom: 10 }}>
          <span style={{ fontSize: 11, color: '#999', fontWeight: 700, minWidth: 56 }}>Search</span>
          <input style={{ ...inputStyle, width: 190 }} value={search} onChange={e => setSearch(e.target.value)} placeholder="Employee ID or name..." />
          <span style={{ fontSize: 11, color: '#999', fontWeight: 700 }}>Status</span>
          <select style={inputStyle} value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
            <option value="">All</option>
            {['submitted','draft','queried','resubmitted'].map(s => <option key={s}>{s}</option>)}
          </select>
          <span style={{ fontSize: 11, color: '#999', fontWeight: 700 }}>Cadre</span>
          <select style={inputStyle} value={cadreFilter} onChange={e => setCadreFilter(e.target.value)}>
            <option value="">All cadres</option>
            {['District','Zone','Multi-Zone','State'].map(c => <option key={c}>{c}</option>)}
          </select>
          <span style={{ fontSize: 11, color: '#999', fontWeight: 700 }}>Designation</span>
          <select style={{ ...inputStyle, minWidth: 160 }} value={desigFilter} onChange={e => setDesigFilter(e.target.value)}>
            <option value="">All designations</option>
            {desigs.map(d => <option key={d} value={d}>{d}</option>)}
          </select>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <span style={{ fontSize: 11, color: '#999', fontWeight: 700, minWidth: 56 }}>Filter</span>
          {[['spouse','Spouse employed'],['census','Census duty'],['disability','Disability'],['medical','Medical grounds']].map(([k, label]) => (
            <label key={k} style={chkLabelStyle(chk[k])}>
              <input type="checkbox" checked={chk[k]} onChange={e => setChk(c => ({ ...c, [k]: e.target.checked }))} style={{ accentColor: '#1a237e' }} />
              {label}
            </label>
          ))}
          <button onClick={resetFilters} style={{ marginLeft: 'auto', background: 'none', border: 'none', fontSize: 12, color: '#999', cursor: 'pointer', fontFamily: 'inherit' }}>Reset all</button>
        </div>
      </div>

      {/* Count + CSV */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <span style={{ fontSize: 12, color: '#666' }}>{filtered.length} record{filtered.length !== 1 ? 's' : ''}{filtered.length !== data.length ? ` (filtered from ${data.length})` : ''}</span>
        <button onClick={downloadCSV} style={{ background: '#1a237e', color: '#fff', border: 'none', borderRadius: 6, padding: '7px 16px', fontSize: 12, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, fontFamily: 'inherit' }}>
          ⬇ Download CSV
        </button>
      </div>

      {/* Table */}
      <div style={{ background: '#fff', border: '1px solid #e0e0e0', borderRadius: 10, overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead>
            <tr style={{ background: '#f5f5f5' }}>
              {['Employee ID','Name','Mobile','Cadre','Designation','Salary Drawing ULB','Status','Pref 1','Pref 2','Pref 3','Pref 4','Pref 5','Station Period','Docs'].map(h => (
                <th key={h} style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 600, fontSize: 11, color: '#666', borderBottom: '1px solid #e0e0e0', whiteSpace: 'nowrap' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr><td colSpan={16} style={{ padding: 32, textAlign: 'center', color: '#bbb' }}>No records match the current filters</td></tr>
            ) : filtered.map((r, i) => {
              const getPref = n => { const p = (r.preferences || []).find(x => x.priority === n); return p ? (p.ulb_name || p.org_name || '—') : '—'; };
              return (
                <tr key={r.id} style={{ background: i % 2 === 0 ? '#fff' : '#fafafa', borderBottom: '1px solid #f0f0f0' }}>
                  <td style={{ padding: '8px 10px', fontWeight: 600, color: '#1a237e', whiteSpace: 'nowrap' }}>{r.employee_id_display}</td>
                  <td style={{ padding: '8px 10px', maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={r.employee_name}>{r.employee_name}</td>
                  <td style={{ padding: '8px 10px', whiteSpace: 'nowrap' }}>{r.mobile_number}</td>
                  <td style={{ padding: '8px 10px', whiteSpace: 'nowrap', fontSize: 11 }}>{r.post_cadre_type}</td>
                  <td style={{ padding: '8px 10px', maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={r.name_of_post}>{r.name_of_post}</td>
                  <td style={{ padding: '8px 10px', maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={r.present_office_name}>{r.present_office_name}</td>
                  <td style={{ padding: '8px 10px' }}>{statusBadge(r.status)}</td>
                  <td style={{ padding: '8px 10px', maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={getPref(1)}>{getPref(1)}</td>
                  <td style={{ padding: '8px 10px', maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={getPref(2)}>{getPref(2)}</td>
                  <td style={{ padding: '8px 10px', maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={getPref(3)}>{getPref(3)}</td>
                  <td style={{ padding: '8px 10px', maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={getPref(4)}>{getPref(4)}</td>
                  <td style={{ padding: '8px 10px', maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={getPref(5)}>{getPref(5)}</td>
                  <td style={{ padding: '8px 10px', whiteSpace: 'nowrap', fontSize: 11 }}>{r.present_station_period_years || 0}y {r.present_station_period_months || 0}m {r.present_station_period_days || 0}d</td>
                  <td style={{ padding: '8px 10px' }}>
                    <button onClick={() => viewDocs(r.id, r.employee_name)} style={{ background: '#e8eaf6', border: '1px solid #c5cae9', color: '#1a237e', padding: '4px 10px', borderRadius: 6, fontSize: 11, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap' }}>
                      📎 Docs
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div style={{ fontSize: 11, color: '#bbb', marginTop: 8, textAlign: 'right' }}>General Transfers 2026 — Government of Telangana</div>
    </div>
  );
}

// ── AdminApplications — wrapper ───────────────────────────
function AdminApplications() {
  return (
    <div>
      <h2 style={{ fontSize: 18, fontWeight: 800, color: '#1a237e', marginBottom: 20 }}>All Applications</h2>
      <ApplicationsTableView />
    </div>
  );
}

// ── District Officer Page ─────────────────────────────────
function DistrictOfficerPage({ onLogout }) {
  const { user } = useAuth();
  return (
    <div style={{ minHeight: '100vh', background: '#f5f5f5', fontFamily: 'inherit' }}>
      <div style={{ background: '#1a237e', color: '#fff', padding: '12px 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <div style={{ fontSize: 16, fontWeight: 800 }}>General Transfers 2026 — District Officer</div>
          <div style={{ fontSize: 12, opacity: 0.75 }}>Government of Telangana · MA Department · CDMA</div>
        </div>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          <span style={{ background: '#e53935', fontSize: 11, padding: '2px 10px', borderRadius: 10, fontWeight: 700 }}>DISTRICT OFFICER</span>
          <span style={{ fontSize: 13 }}>{user?.employeeId}</span>
          <button onClick={onLogout} style={{ background: 'rgba(255,255,255,.15)', border: 'none', color: '#fff', padding: '6px 14px', borderRadius: 6, cursor: 'pointer', fontSize: 12 }}>← Logout</button>
        </div>
      </div>
      <div style={{ padding: 24 }}>
        <h2 style={{ fontSize: 18, fontWeight: 800, color: '#1a237e', marginBottom: 20 }}>District Applications — CDMA</h2>
        <ApplicationsTableView deptFilter="CDMA" cadreFilter="District" districtFilter={user?.districtId} />
      </div>
    </div>
  );
}


// ── Scrutiny Officer Page ─────────────────────────────────
function ScrutinyOfficerPage({ dept, onLogout }) {
  const { user } = useAuth();
  const deptLabel = dept === 'CDMA' ? 'CDMA' : 'DTCP';
  return (
    <div style={{ minHeight: '100vh', background: '#f5f5f5', fontFamily: 'inherit' }}>
      <div style={{ background: '#1a237e', color: '#fff', padding: '12px 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <div style={{ fontSize: 16, fontWeight: 800 }}>General Transfers 2026 — {deptLabel} Scrutiny Officer</div>
          <div style={{ fontSize: 12, opacity: 0.75 }}>Government of Telangana · MA Department</div>
        </div>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          <span style={{ background: '#e53935', fontSize: 11, padding: '2px 10px', borderRadius: 10, fontWeight: 700 }}>{deptLabel} SCRUTINY</span>
          <span style={{ fontSize: 13 }}>{user?.employeeId}</span>
          <button onClick={onLogout} style={{ background: 'rgba(255,255,255,.15)', border: 'none', color: '#fff', padding: '6px 14px', borderRadius: 6, cursor: 'pointer', fontSize: 12 }}>← Logout</button>
        </div>
      </div>
      <div style={{ padding: 24 }}>
        <h2 style={{ fontSize: 18, fontWeight: 800, color: '#1a237e', marginBottom: 20 }}>{deptLabel} Applications</h2>
        <ApplicationsTableView deptFilter={dept} />
      </div>
    </div>
  );
}

// ── Admin Users ───────────────────────────────────────────
function AdminUsers() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [addForm, setAddForm] = useState({ employeeId: '', deptId: '' });
  const [addLoading, setAddLoading] = useState(false);
  const [departments, setDepartments] = useState([]);
  const [bulkResult, setBulkResult] = useState(null);

  useEffect(() => {
    mastersAPI.getDepartments()
      .then(r => setDepartments(r.data || []))
      .catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminAPI.getUsers({ page, limit: 20, search });
      setData(res.data.data);
      setTotal(res.data.total);
    } catch { toast.error('Failed to load users'); }
    finally { setLoading(false); }
  }, [page, search]);

  useEffect(() => { load(); }, [load]);

  const handleReset = async (id) => {
    if (!window.confirm('Reset password to default?')) return;
    try {
      const res = await adminAPI.resetPassword(id);
      toast.success(`Password reset. Default: ${res.data.defaultPassword}`);
    } catch { toast.error('Reset failed'); }
  };

  const handleToggle = async (id) => {
    try {
      await adminAPI.toggleActive(id);
      toast.success('Account status updated');
      load();
    } catch { toast.error('Update failed'); }
  };

  const handleUnlock = async (id) => {
    try {
      await adminAPI.unlockAccount(id);
      toast.success('Account unlocked');
      load();
    } catch { toast.error('Unlock failed'); }
  };

  const handleAddEmployee = async () => {
    if (!addForm.employeeId.trim()) { toast.error('Employee ID is required'); return; }
    if (!addForm.deptId) { toast.error('Department is required'); return; }
    setAddLoading(true);
    try {
      const res = await adminAPI.createUser({ ...addForm, deptId: parseInt(addForm.deptId, 10) });
      toast.success(`Account created. Default password: ${res.data.defaultPassword}`);
      setShowAddModal(false);
      setAddForm({ employeeId: '', deptId: '' });
      load();
    } catch (err) {
      const msg = err.response?.data?.error
        || err.response?.data?.errors?.[0]?.msg
        || err.message
        || 'Failed to create account';
      toast.error(msg);
    } finally { setAddLoading(false); }
  };

  const handleBulkUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    // Reset input so same file can be re-uploaded
    e.target.value = '';
    try {
      const text = await file.text();
      const isCSV = file.name.toLowerCase().endsWith('.csv');
      let employees = [];

      if (isCSV) {
        // Parse CSV
        const lines = text.split(/\r?\n/).filter(l => l.trim());
        if (!lines.length) { toast.error('Empty file'); return; }
        const headers = lines[0].split(',').map(h => h.trim().toLowerCase().replace(/['"]/g,''));
        const empIdx  = headers.findIndex(h => ['employee_id','employeeid','emp_id','empid'].includes(h));
        const deptIdx = headers.findIndex(h => ['dept_id','deptid','department_id','departmentid'].includes(h));
        if (empIdx === -1) { toast.error('CSV must have Employee_ID column'); return; }
        employees = lines.slice(1).map(line => {
          const cols = line.split(',').map(c => c.trim().replace(/['"]/g,''));
          return {
            employeeId: (cols[empIdx] || '').toUpperCase().trim(),
            deptId: parseInt(cols[deptIdx] || '1') || 1,
          };
        }).filter(e => e.employeeId);
      } else {
        // Parse Excel using xlsx library
        try {
          const XLSX = await import('xlsx');
          const data = await file.arrayBuffer();
          const wb = XLSX.read(data);
          const rows = XLSX.utils.sheet_to_json(wb.Sheets[wb.SheetNames[0]]);
          employees = rows.map(r => ({
            employeeId: String(r['Employee_ID'] || r['employee_id'] || r['EmployeeID'] || r['Emp_ID'] || '').trim().toUpperCase(),
            deptId: parseInt(r['dept_id'] || r['Dept_ID'] || r['DeptID'] || r['Department_ID'] || 1) || 1,
          })).filter(e => e.employeeId);
        } catch(xlsxErr) {
          toast.error('Excel parsing failed. Please use CSV format instead.');
          return;
        }
      }

      if (!employees.length) { toast.error('No valid employee rows found. Check column names: Employee_ID, dept_id'); return; }
      toast.loading(`Importing ${employees.length} employees...`, { id: 'bulk' });
      console.log('Sending employees:', JSON.stringify(employees.slice(0,3)));
      const res = await adminAPI.bulkImport({ employees });
      toast.dismiss('bulk');
      setBulkResult(res.data);
      toast.success(`✅ Created: ${res.data.created}  |  ⏭ Skipped: ${res.data.skipped}`);
      load();
    } catch (err) {
      toast.dismiss('bulk');
      toast.error('Bulk import failed: ' + (err.response?.data?.error || err.message || 'Unknown error'));
    }
  };

  const modalStyle = {
    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
    background: 'rgba(0,0,0,.6)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 9999,
  };
  const boxStyle = {
    background: '#fff', borderRadius: 16, padding: '32px 28px',
    width: '100%', maxWidth: 460,
    boxShadow: '0 24px 80px rgba(0,0,0,.35)',
    maxHeight: '90vh', overflowY: 'auto',
  };

  return (
    <div>
      <div style={{ marginBottom: 20, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h2 style={{ fontSize: 20, fontWeight: 800, color: '#1a237e' }}>Employee Accounts</h2>
          <p style={{ fontSize: 13, color: '#757575' }}>Total: {total} accounts</p>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button className="btn-admin primary" onClick={() => setShowAddModal(true)}>+ Add Employee</button>
          <button className="btn-admin success" onClick={() => setShowBulkModal(true)}>⬆ Bulk Import</button>
        </div>
      </div>
      <div className="admin-table-wrap" style={{ padding: '12px 16px', marginBottom: 16 }}>
        <input value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} placeholder="Search Employee ID…" style={{ padding: '8px 12px', border: '1.5px solid #e0e0e0', borderRadius: 6, fontSize: 13, width: 280, fontFamily: 'inherit' }} />
      </div>
      <div className="admin-table-wrap">
        <div className="admin-table-scroll">
          {loading ? <div style={{ padding: 40, textAlign: 'center' }}>Loading…</div> : (
            <table className="atable">
              <thead><tr><th>Employee ID</th><th>Department</th><th>Status</th><th>App Status</th><th>Last Login</th><th>Actions</th></tr></thead>
              <tbody>
                {data.map(u => (
                  <tr key={u.id}>
                    <td>{u.employee_id}</td>
                    <td>{u.dept_name || '—'}</td>
                    <td>
                      <span className={`badge-status ${u.is_active ? 'submitted' : 'draft'}`}>{u.is_active ? 'Active' : 'Inactive'}</span>
                      {u.locked_until && new Date(u.locked_until) > new Date() && <span className="badge-status draft" style={{ marginLeft: 4 }}>Locked</span>}
                    </td>
                    <td>{u.app_status ? <span className={`badge-status ${u.app_status}`}>{u.app_status}</span> : '—'}</td>
                    <td>{u.last_login ? new Date(u.last_login).toLocaleString('en-IN') : 'Never'}</td>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      <button className="btn-admin secondary sm" onClick={() => handleReset(u.id)} style={{ marginRight: 4 }}>🔑 Reset</button>
                      <button className={`btn-admin sm ${u.is_active ? 'warning' : 'success'}`} onClick={() => handleToggle(u.id)} style={{ marginRight: 4 }}>
                        {u.is_active ? '🚫' : '✅'}
                      </button>
                      {u.locked_until && new Date(u.locked_until) > new Date() && (
                        <button className="btn-admin success sm" onClick={() => handleUnlock(u.id)}>🔓</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        <div className="pagination">
          <span style={{ fontSize: 12, color: '#757575' }}>Total: {total}</span>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn-admin secondary" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}>← Prev</button>
            <span style={{ padding: '6px 12px', fontSize: 12 }}>Page {page}</span>
            <button className="btn-admin secondary" onClick={() => setPage(p => p + 1)} disabled={data.length < 20}>Next →</button>
          </div>
        </div>
      </div>

      {/* ── Add Employee Modal ── */}
      {showAddModal && (
        <div style={modalStyle} onClick={() => setShowAddModal(false)}>
          <div style={boxStyle} onClick={e => e.stopPropagation()}>
            <h3 style={{ fontSize: 17, fontWeight: 800, color: '#1a237e', marginBottom: 20 }}>+ Add New Employee</h3>

            <div className="form-group" style={{ marginBottom: 16 }}>
              <label style={{ fontSize: 12, fontWeight: 700, color: '#555', textTransform: 'uppercase', letterSpacing: .5, display: 'block', marginBottom: 6 }}>
                Employee ID <span style={{ color: '#c62828' }}>*</span>
              </label>
              <input
                type="text"
                value={addForm.employeeId}
                onChange={e => setAddForm(f => ({ ...f, employeeId: e.target.value.toUpperCase() }))}
                placeholder="e.g. EMP001"
                style={{ width: '100%', padding: '10px 12px', border: '1.5px solid #e0e0e0', borderRadius: 8, fontSize: 14, fontFamily: 'inherit', boxSizing: 'border-box' }}
              />
            </div>

            <div className="form-group" style={{ marginBottom: 24 }}>
              <label style={{ fontSize: 12, fontWeight: 700, color: '#555', textTransform: 'uppercase', letterSpacing: .5, display: 'block', marginBottom: 6 }}>
                Department <span style={{ color: '#c62828' }}>*</span>
              </label>
              <select
                value={addForm.deptId}
                onChange={e => setAddForm(f => ({ ...f, deptId: e.target.value }))}
                style={{ width: '100%', padding: '10px 12px', border: '1.5px solid #e0e0e0', borderRadius: 8, fontSize: 14, fontFamily: 'inherit' }}
              >
                <option value="">— Select Department —</option>
                {departments.map(d => (
                  <option key={d.id} value={d.id}>{d.dept_name} ({d.dept_code})</option>
                ))}
              </select>
            </div>

            <div style={{ background: '#f5f5f5', borderRadius: 8, padding: '10px 14px', marginBottom: 20, fontSize: 12, color: '#555' }}>
              Default password will be: <strong style={{ color: '#1a237e' }}>TG@[EmployeeID]</strong><br />
              Employee must change it on first login.
            </div>

            <div style={{ display: 'flex', gap: 10 }}>
              <button
                className="btn-admin secondary"
                onClick={() => { setShowAddModal(false); setAddForm({ employeeId: '', deptId: '' }); }}
                style={{ flex: 1, padding: '10px 0' }}
              >Cancel</button>
              <button
                className="btn-admin primary"
                onClick={handleAddEmployee}
                disabled={addLoading}
                style={{ flex: 2, padding: '10px 0' }}
              >{addLoading ? '⏳ Creating…' : 'Create Account'}</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Bulk Import Modal ── */}
      {showBulkModal && (
        <div style={modalStyle} onClick={() => setShowBulkModal(false)}>
          <div style={boxStyle} onClick={e => e.stopPropagation()}>
            <h3 style={{ fontSize: 17, fontWeight: 800, color: '#1a237e', marginBottom: 16 }}>⬆ Bulk Import Employees</h3>

            <div style={{ background: '#e8eaf6', borderRadius: 8, padding: '12px 14px', marginBottom: 20, fontSize: 12, color: '#333', lineHeight: 1.7 }}>
              <strong>Supported formats: Excel (.xlsx) or CSV</strong><br />
              Columns: <code>Employee_ID</code>, <code>dept_id</code><br />
              dept_id: 1=CDMA &nbsp;|&nbsp; 2=EnC(PH) &nbsp;|&nbsp; 3=DTCP<br />
              Default password: <strong>TG@[EmployeeID]</strong><br /><br />
              <strong>CSV Example:</strong><br />
              <code>Employee_ID,dept_id</code><br />
              <code>EMP001,1</code><br />
              <code>EMP002,3</code>
            </div>

            <label style={{
              display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10,
              padding: '24px', border: '2px dashed #9fa8da', borderRadius: 10,
              cursor: 'pointer', background: '#f8f9ff', marginBottom: 16,
            }}>
              <span style={{ fontSize: 32 }}>📂</span>
              <span style={{ fontSize: 13, fontWeight: 600, color: '#3949ab' }}>Click to select Excel (.xlsx) or CSV file</span>
              <input type="file" accept=".xlsx,.xls,.csv" onChange={handleBulkUpload} style={{ display: 'none' }} />
            </label>

            {bulkResult && (
              <div style={{ background: '#e8f5e9', borderRadius: 8, padding: '12px 14px', marginBottom: 16, fontSize: 13 }}>
                ✅ Created: <strong>{bulkResult.created}</strong> &nbsp;|&nbsp;
                ⏭ Skipped: <strong>{bulkResult.skipped}</strong>
                {bulkResult.errors?.length > 0 && (
                  <div style={{ marginTop: 6, color: '#c62828', fontSize: 11 }}>
                    Errors: {bulkResult.errors.slice(0, 3).map(e => e.employeeId).join(', ')}
                    {bulkResult.errors.length > 3 ? ` +${bulkResult.errors.length - 3} more` : ''}
                  </div>
                )}
              </div>
            )}

            <button
              className="btn-admin secondary"
              onClick={() => { setShowBulkModal(false); setBulkResult(null); }}
              style={{ width: '100%', padding: '10px 0' }}
            >Close</button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Generic Master CRUD ───────────────────────────────────
function MasterCRUD({ title, icon, columns, fetchData, createFn, updateFn, deleteFn, FormFields }) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modal, setModal] = useState(null); // null | { mode: 'add'|'edit', record: {} }

  const load = useCallback(async () => {
    setLoading(true);
    try { const res = await fetchData(); setData(res.data); }
    catch { toast.error('Failed to load data'); }
    finally { setLoading(false); }
  }, [fetchData]);

  useEffect(() => { load(); }, [load]);

  const handleSave = async (formData) => {
    try {
      if (modal.mode === 'add') await createFn(formData);
      else await updateFn(modal.record.id, formData);
      toast.success('Saved successfully');
      setModal(null);
      load();
    } catch { toast.error('Save failed'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this record?')) return;
    try { await deleteFn(id); toast.success('Deleted'); load(); }
    catch { toast.error('Delete failed'); }
  };

  return (
    <div>
      <div style={{ marginBottom: 20, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h2 style={{ fontSize: 20, fontWeight: 800, color: '#1a237e' }}>{icon} {title}</h2>
        <button className="btn-admin primary" onClick={() => setModal({ mode: 'add', record: {} })}>+ Add New</button>
      </div>
      <div className="admin-table-wrap">
        <div className="admin-table-scroll">
          {loading ? <div style={{ padding: 40, textAlign: 'center' }}>Loading…</div> : (
            <table className="atable">
              <thead><tr>{columns.map(c => <th key={c.key}>{c.label}</th>)}<th>Actions</th></tr></thead>
              <tbody>
                {data.map(row => (
                  <tr key={row.id}>
                    {columns.map(c => <td key={c.key}>{c.render ? c.render(row) : row[c.key]}</td>)}
                    <td style={{ whiteSpace: 'nowrap' }}>
                      <button className="btn-admin secondary sm" onClick={() => setModal({ mode: 'edit', record: row })} style={{ marginRight: 4 }}>✏️</button>
                      <button className="btn-admin danger sm" onClick={() => handleDelete(row.id)}>🗑</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
      {modal && (
        <div className="amodal-overlay" onClick={() => setModal(null)}>
          <div className="amodal" onClick={e => e.stopPropagation()}>
            <h3>{modal.mode === 'add' ? 'Add' : 'Edit'} {title.split(' ')[0]}</h3>
            <FormFields record={modal.record} onSave={handleSave} onCancel={() => setModal(null)} />
          </div>
        </div>
      )}
    </div>
  );
}

// ── Master Zones ──────────────────────────────────────────
function MasterZones() {
  return (
    <MasterCRUD
      title="Multi Zones & Zones" icon="🗺️"
      columns={[{ key: 'id', label: 'ID' }, { key: 'name', label: 'Name' }]}
      fetchData={mastersAPI.getMultiZones}
      createFn={(d) => mastersAPI.createMultiZone(d)}
      updateFn={(id, d) => mastersAPI.updateMultiZone(id, d)}
      deleteFn={mastersAPI.deleteMultiZone}
      FormFields={({ record, onSave, onCancel }) => {
        const [name, setName] = useState(record.name || '');
        return (
          <div>
            <div className="form-row full"><div><label>Name</label><input value={name} onChange={e => setName(e.target.value)} /></div></div>
            <div className="amodal-footer">
              <button className="btn-admin secondary" onClick={onCancel}>Cancel</button>
              <button className="btn-admin primary" onClick={() => onSave({ name })}>Save</button>
            </div>
          </div>
        );
      }}
    />
  );
}

// ── Master Districts ──────────────────────────────────────
function MasterDistricts() {
  const [zones, setZones] = useState([]);
  useEffect(() => { mastersAPI.getZones().then(r => setZones(r.data)).catch(() => {}); }, []);

  return (
    <MasterCRUD
      title="Districts" icon="📍"
      columns={[{ key: 'id', label: 'ID' }, { key: 'name', label: 'Name' }, { key: 'zone_id', label: 'Zone', render: r => zones.find(z => z.id === r.zone_id)?.name || r.zone_id }]}
      fetchData={mastersAPI.getAllDistricts}
      createFn={(d) => mastersAPI.createDistrict(d)}
      updateFn={(id, d) => mastersAPI.updateDistrict(id, d)}
      deleteFn={mastersAPI.deleteDistrict}
      FormFields={({ record, onSave, onCancel }) => {
        const [name, setName] = useState(record.name || '');
        const [zoneId, setZoneId] = useState(record.zone_id || '');
        return (
          <div>
            <div className="form-row"><div><label>Name</label><input value={name} onChange={e => setName(e.target.value)} /></div><div><label>Zone</label><select value={zoneId} onChange={e => setZoneId(e.target.value)}><option value="">— Select —</option>{zones.map(z => <option key={z.id} value={z.id}>{z.name}</option>)}</select></div></div>
            <div className="amodal-footer"><button className="btn-admin secondary" onClick={onCancel}>Cancel</button><button className="btn-admin primary" onClick={() => onSave({ name, zoneId })}>Save</button></div>
          </div>
        );
      }}
    />
  );
}

// ── Master ULBs ───────────────────────────────────────────
function MasterULBs() {
  const [districts, setDistricts] = useState([]);
  useEffect(() => { mastersAPI.getAllDistricts().then(r => setDistricts(r.data)).catch(() => {}); }, []);

  return (
    <MasterCRUD
      title="Urban Local Bodies" icon="🏙️"
      columns={[{ key: 'id', label: 'ID' }, { key: 'name', label: 'ULB Name' }, { key: 'district_name', label: 'District' }]}
      fetchData={mastersAPI.getAllULBs}
      createFn={(d) => mastersAPI.createULB(d)}
      updateFn={(id, d) => mastersAPI.updateULB(id, d)}
      deleteFn={mastersAPI.deleteULB}
      FormFields={({ record, onSave, onCancel }) => {
        const [name, setName] = useState(record.name || '');
        const [districtId, setDistrictId] = useState(record.district_id || '');
        return (
          <div>
            <div className="form-row"><div><label>ULB Name</label><input value={name} onChange={e => setName(e.target.value)} /></div><div><label>District</label><select value={districtId} onChange={e => setDistrictId(e.target.value)}><option value="">— Select —</option>{districts.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}</select></div></div>
            <div className="amodal-footer"><button className="btn-admin secondary" onClick={onCancel}>Cancel</button><button className="btn-admin primary" onClick={() => onSave({ name, districtId })}>Save</button></div>
          </div>
        );
      }}
    />
  );
}

// ── Master Cadres ─────────────────────────────────────────
function MasterCadres() {
  return (
    <MasterCRUD
      title="Post Cadres" icon="🎖️"
      columns={[{ key: 'id', label: 'ID' }, { key: 'name_of_post', label: 'Post Name' }, { key: 'cadre_type', label: 'Cadre Type' }, { key: 'appointing_authority', label: 'Appointing Authority' }]}
      fetchData={mastersAPI.getAllCadres}
      createFn={(d) => mastersAPI.createCadre(d)}
      updateFn={(id, d) => mastersAPI.updateCadre(id, d)}
      deleteFn={mastersAPI.deleteCadre}
      FormFields={({ record, onSave, onCancel }) => {
        const [nameOfPost, setNameOfPost] = useState(record.name_of_post || '');
        const [cadreType, setCadreType] = useState(record.cadre_type || '');
        const [appointingAuthority, setAppointingAuthority] = useState(record.appointing_authority || '');
        return (
          <div>
            <div className="form-row full"><div><label>Name of Post</label><input value={nameOfPost} onChange={e => setNameOfPost(e.target.value)} /></div></div>
            <div className="form-row">
              <div><label>Cadre Type</label><select value={cadreType} onChange={e => setCadreType(e.target.value)}><option value="">— Select —</option>{['State', 'Multi-Zone', 'Zone', 'District'].map(t => <option key={t}>{t}</option>)}</select></div>
              <div><label>Appointing Authority</label><input value={appointingAuthority} onChange={e => setAppointingAuthority(e.target.value)} /></div>
            </div>
            <div className="amodal-footer"><button className="btn-admin secondary" onClick={onCancel}>Cancel</button><button className="btn-admin primary" onClick={() => onSave({ nameOfPost, cadreType, appointingAuthority })}>Save</button></div>
          </div>
        );
      }}
    />
  );
}

// ── Master Organizations (DTCP) ───────────────────────────
// Supports CRUD + Excel upload for organizations master data.
// Excel columns expected: org_code, dept_id, designation, cadre_category, org_name
function MasterOrganizations() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modal, setModal] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const fileInputRef = React.useRef();

  const load = async () => {
    setLoading(true);
    try {
      const res = await mastersAPI.getOrganizations(3); // DTCP dept_id=3
      setData(res.data || []);
    } catch { toast.error('Failed to load organizations'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const handleSave = async (formData) => {
    try {
      if (modal.mode === 'add') await mastersAPI.createOrganization(formData);
      else await mastersAPI.updateOrganization(modal.record.id, formData);
      toast.success('Saved successfully');
      setModal(null);
      load();
    } catch { toast.error('Save failed'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Deactivate this organization?')) return;
    try { await mastersAPI.deleteOrganization(id); toast.success('Deleted'); load(); }
    catch { toast.error('Delete failed'); }
  };

  // Excel upload — parse client-side using SheetJS, send rows to backend
  const handleExcelUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    setUploadResult(null);
    try {
      const XLSX = await import('xlsx');
      const data = await file.arrayBuffer();
      const wb = XLSX.read(data);
      const ws = wb.Sheets[wb.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json(ws, { defval: '' });
      // Normalise column names to snake_case
      const normalised = rows.map(r => ({
        org_code:       String(r.org_code || r['Org Code'] || r['OrgCode'] || '').trim(),
        dept_id:        Number(r.dept_id || r['Dept ID'] || r['DeptId'] || 3),
        designation:    String(r.designation || r['Designation'] || '').trim(),
        cadre_category: String(r.cadre_category || r['Cadre Category'] || r['CadreCategory'] || '').trim(),
        org_name:       String(r.org_name || r['Org Name'] || r['OrgName'] || '').trim(),
      }));
      const res = await mastersAPI.uploadOrganizations(normalised);
      setUploadResult(res.data);
      toast.success(`Upload complete — ${res.data.inserted} inserted, ${res.data.updated} updated`);
      load();
    } catch (err) {
      toast.error('Upload failed: ' + (err.response?.data?.error || err.message));
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  return (
    <div>
      <div style={{ marginBottom: 20, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h2 style={{ fontSize: 20, fontWeight: 800, color: '#1a237e' }}>🏛️ Organizations (DTCP)</h2>
          <p style={{ fontSize: 13, color: '#757575' }}>Total: {data.length} organizations</p>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          {/* Excel upload */}
          <input ref={fileInputRef} type="file" accept=".xlsx,.xls" onChange={handleExcelUpload} style={{ display: 'none' }} />
          <button className="btn-admin secondary" onClick={() => fileInputRef.current.click()} disabled={uploading}>
            {uploading ? '⏳ Uploading…' : '⬆ Upload Excel'}
          </button>
          <button className="btn-admin primary" onClick={() => setModal({ mode: 'add', record: {} })}>+ Add New</button>
        </div>
      </div>

      {/* Upload result summary */}
      {uploadResult && (
        <div style={{ marginBottom: 16, padding: '10px 14px', background: '#e8f5e9', borderRadius: 8, border: '1px solid #a5d6a7', fontSize: 13 }}>
          ✅ Inserted: <strong>{uploadResult.inserted}</strong> &nbsp;|&nbsp;
          Updated: <strong>{uploadResult.updated}</strong>
          {uploadResult.errors?.length > 0 && (
            <div style={{ marginTop: 6, color: '#c62828' }}>
              ⚠ {uploadResult.errors.length} row(s) skipped — missing required fields
            </div>
          )}
        </div>
      )}

      {/* Excel format hint */}
      <div style={{ marginBottom: 16, padding: '8px 14px', background: '#e3f2fd', borderRadius: 6, fontSize: 12, color: '#1565c0' }}>
        📋 Excel columns expected: <strong>org_code, dept_id, designation, cadre_category, org_name</strong>
      </div>

      <div className="admin-table-wrap">
        <div className="admin-table-scroll">
          {loading ? <div style={{ padding: 40, textAlign: 'center' }}>Loading…</div> : (
            <table className="atable">
              <thead>
                <tr>
                  <th>ID</th><th>Org Code</th><th>Designation</th>
                  <th>Cadre Category</th><th>Org Name</th><th>Active</th><th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {data.map(row => (
                  <tr key={row.id}>
                    <td>{row.id}</td>
                    <td>{row.org_code}</td>
                    <td style={{ maxWidth: 200, whiteSpace: 'normal', fontSize: 12 }}>{row.designation}</td>
                    <td>{row.cadre_category}</td>
                    <td>{row.org_name}</td>
                    <td>{row.is_active ? '✅' : '❌'}</td>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      <button className="btn-admin secondary sm" onClick={() => setModal({ mode: 'edit', record: row })} style={{ marginRight: 4 }}>✏️</button>
                      <button className="btn-admin danger sm" onClick={() => handleDelete(row.id)}>🗑</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Add / Edit modal */}
      {modal && (
        <div className="amodal-overlay" onClick={() => setModal(null)}>
          <div className="amodal" onClick={e => e.stopPropagation()}>
            <h3>{modal.mode === 'add' ? 'Add' : 'Edit'} Organization</h3>
            <OrgFormFields record={modal.record} onSave={handleSave} onCancel={() => setModal(null)} />
          </div>
        </div>
      )}
    </div>
  );
}

function OrgFormFields({ record, onSave, onCancel }) {
  const [orgCode,       setOrgCode]       = useState(record.org_code || '');
  const [designation,   setDesignation]   = useState(record.designation || '');
  const [cadreCategory, setCadreCategory] = useState(record.cadre_category || '');
  const [orgName,       setOrgName]       = useState(record.org_name || '');
  const [isActive,      setIsActive]      = useState(record.is_active !== false);

  const dtcpDesignations = [
    'Assistant Director/Assistant City Planner/DTCPO/APO',
    'Town Planning Officer',
    'Town Planning Supervisor',
    'Town Planning Building Overseer',
  ];

  return (
    <div>
      <div className="form-row">
        <div>
          <label>Org Code <span style={{ color: 'red' }}>*</span></label>
          <input value={orgCode} onChange={e => setOrgCode(e.target.value)} placeholder="e.g. ORGID95" />
        </div>
        <div>
          <label>Active</label>
          <select value={isActive ? 'yes' : 'no'} onChange={e => setIsActive(e.target.value === 'yes')}>
            <option value="yes">Yes</option>
            <option value="no">No</option>
          </select>
        </div>
      </div>
      <div className="form-row full">
        <div>
          <label>Designation <span style={{ color: 'red' }}>*</span></label>
          <select value={designation} onChange={e => setDesignation(e.target.value)}>
            <option value="">— Select —</option>
            {dtcpDesignations.map(d => <option key={d} value={d}>{d}</option>)}
          </select>
        </div>
      </div>
      <div className="form-row">
        <div>
          <label>Cadre Category <span style={{ color: 'red' }}>*</span></label>
          <input value={cadreCategory} onChange={e => setCadreCategory(e.target.value)} placeholder="e.g. Multi Zone-I" />
        </div>
        <div>
          <label>Org Name <span style={{ color: 'red' }}>*</span></label>
          <input value={orgName} onChange={e => setOrgName(e.target.value)} placeholder="e.g. Karimnagar Municipality" />
        </div>
      </div>
      <div className="amodal-footer">
        <button className="btn-admin secondary" onClick={onCancel}>Cancel</button>
        <button className="btn-admin primary" onClick={() => onSave({
          orgCode, deptId: 3, designation, cadreCategory, orgName, isActive,
        })}>Save</button>
      </div>
    </div>
  );
}

// ── DTCP Subsection B — Cadre Category + ULB/Organisation ─
function DTCPSubsectionB({ form, setField, masters }) {
  const [cadreCategories, setCadreCategories] = React.useState([]);
  const [orgs, setOrgs] = React.useState([]);
  const prevNameOfPost = React.useRef('');
  const isFirstLoad = React.useRef(true);
  const prevCadreCategory = React.useRef(form.cadre_category || '');

  const selectedCadre = masters.cadres?.find(c => String(c.id) === String(form.cadre_id));
  const nameOfPost = selectedCadre?.name_of_post || '';
  const deptId = '3';

  // Load cadre categories when designation changes
  React.useEffect(() => {
    if (!nameOfPost) { setCadreCategories([]); setOrgs([]); return; }
    mastersAPI.getOrgCadreCategories(deptId, nameOfPost)
      .then(r => {
        const cats = r.data || [];
        setCadreCategories(cats);
        const userChangedDesig = prevNameOfPost.current !== nameOfPost && !isFirstLoad.current;
        prevNameOfPost.current = nameOfPost;
        isFirstLoad.current = false;
        if (userChangedDesig) {
          setField('cadre_category', cats.length === 1 ? cats[0] : '');
          setField('org_id', '');
          setField('org_name', '');
          setField('present_office_name', '');
        } else {
          if (!form.cadre_category && cats.length === 1) setField('cadre_category', cats[0]);
        }
      })
      .catch(() => {});
  }, [nameOfPost]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load orgs when cadre category changes
  React.useEffect(() => {
    if (!nameOfPost || !form.cadre_category) { setOrgs([]); return; }
    mastersAPI.getOrgOrgs(deptId, nameOfPost, form.cadre_category)
      .then(r => {
        setOrgs(r.data || []);
        if (prevCadreCategory.current !== form.cadre_category) {
          prevCadreCategory.current = form.cadre_category;
          setField('org_id', '');
          setField('org_name', '');
          setField('present_office_name', '');
        }
      })
      .catch(() => {});
  }, [form.cadre_category, nameOfPost]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="card" style={{ borderTop: '3px solid var(--teal-mid)' }}>
      <div className="card-header">
        <div className="section-icon">🏛</div>
        <div>
          <h2 style={{ marginBottom: 2 }}>SECTION I — Employee Information</h2>
          <p style={{ fontSize: 11.5, color: 'var(--text-muted)', fontWeight: 500, margin: 0 }}>Subsection B — DTCP Present Working Station</p>
        </div>
      </div>
      <p style={{ fontSize: 12.5, color: 'var(--text-muted)', marginBottom: 18, padding: '8px 12px', background: 'var(--teal-pale)', borderRadius: 6, borderLeft: '3px solid var(--teal-mid)' }}>
        This information is used to identify the employee's present working station for transfer processing.
      </p>
      <div className="form-grid">
        <div className="form-group">
          <label>k. Cadre Category <span className="required">*</span></label>
          <select value={form.cadre_category || ''} onChange={e => setField('cadre_category', e.target.value)} disabled={cadreCategories.length === 0}>
            <option value="">— Select Cadre Category —</option>
            {cadreCategories.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <div className="form-group">
          <label>l. ULB / Organisation <span className="required">*</span></label>
          <select value={form.org_id || ''} onChange={e => {
            const orgId = e.target.value;
            setField('org_id', orgId);
            const org = orgs.find(o => String(o.id) === String(orgId));
            if (org) {
              setField('org_name', org.org_name);
              // Auto-fill Section II — Organisation
              setField('present_office_name', org.org_name);
            }
            if (!orgId) {
              setField('org_name', '');
              setField('present_office_name', '');
            }
          }} disabled={orgs.length === 0}>
            <option value="">— Select Organisation —</option>
            {orgs.map(o => <option key={o.id} value={o.id}>{o.org_name}</option>)}
          </select>
          {form.org_id && form.org_name && (
            <span className="autofill-note">📄 Auto-fills Section II — Name of ULB/Organisation</span>
          )}
        </div>
      </div>
    </div>
  );
}


// ── FileUploadArea Component ──────────────────────────────
function FileUploadArea({ label, docType, application, uploading, onFileUpload }) {
  const urlField = docType + '_url';
  const keyField = docType + '_s3_key';
  const presignedUrl = application?.[urlField];
  const s3Key = application?.[keyField];
  const isUploading = uploading?.[docType];

  const handleView = () => {
    if (presignedUrl) {
      window.open(presignedUrl, '_blank', 'noopener,noreferrer');
    } else {
      toast.error('Document URL not available. Please refresh the page and try again.');
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    e.target.value = '';
    if (file.size > 10 * 1024 * 1024) { toast.error('File too large — max 10MB'); return; }
    onFileUpload(docType, file);
  };

  return (
    <div style={{ border: '1.5px dashed #e0e0e0', borderRadius: 8, padding: 14, background: s3Key ? '#f1f8e9' : '#fafafa' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 22 }}>{s3Key ? '✅' : '📎'}</span>
          <div>
            <div style={{ fontSize: 13, fontWeight: 600, color: '#333' }}>{label}</div>
            {s3Key
              ? <div style={{ fontSize: 11, color: '#2e7d32' }}>Uploaded successfully</div>
              : <div style={{ fontSize: 11, color: '#9e9e9e' }}>PDF, JPG, PNG — Max 10MB</div>
            }
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {(s3Key || presignedUrl) && (
            <button onClick={handleView} style={{ background: '#e3f2fd', border: '1px solid #90caf9', color: '#1565c0', padding: '6px 14px', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>
              👁 View
            </button>
          )}
          <label style={{ background: '#1a237e', color: '#fff', padding: '6px 14px', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
            {isUploading ? '⏳ Uploading...' : s3Key ? '🔄 Replace' : '⬆ Upload'}
            <input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={handleFileChange} style={{ display: 'none' }} disabled={isUploading} />
          </label>
        </div>
      </div>
    </div>
  );
}


// ── Section II — Present Post ─────────────────────────────
function Section2({ form, setField, masters, application, uploading, onFileUpload, onSave, saving, onBack, onNext }) {
  const { user } = useAuth();
  const isDTCP = String(user?.deptId) === '3';
  const today = new Date().toISOString().split('T')[0];
  const ulbLabel = isDTCP ? 'ULB / Organisation (Salary Drawing)' : 'Name of ULB (Salary Drawing ULB)';

  return (
    <div className="page-container">
      <div className="card">
        <div className="card-header">
          <div className="section-icon">🏢</div>
          <div>
            <h2 style={{ marginBottom: 2 }}>SECTION II — Details of Present Post</h2>
            <p style={{ fontSize: 11.5, color: 'var(--text-muted)', fontWeight: 500, margin: 0 }}>Present post and working station information</p>
          </div>
        </div>
        <div className="form-grid">
          <div className="form-group span2">
            <label>a. {ulbLabel} <span className="required">*</span></label>
            <input
              type="text"
              value={form.present_office_name || ''}
              onChange={e => setField('present_office_name', e.target.value)}
              placeholder={`Auto-filled from Section I — ${ulbLabel}`}
              style={{ background: form.present_office_name ? 'var(--teal-pale)' : '#fff', fontWeight: form.present_office_name ? 500 : 400 }}
            />
            <span className="autofill-note">📄 Auto-filled from Section I — {ulbLabel}</span>
          </div>
          <div className="form-group span2">
            <label>b. Name of Present Post <span className="required">*</span></label>
            <input
              type="text"
              value={form.present_post_name || ''}
              onChange={e => setField('present_post_name', e.target.value)}
              placeholder="Auto-filled from Section I — Designation"
              style={{ background: form.present_post_name ? 'var(--teal-pale)' : '#fff', fontWeight: form.present_post_name ? 500 : 400 }}
            />
            <span className="autofill-note">📄 Auto-filled from Section I — Name of Post (Designation)</span>
          </div>
          <div className="form-group">
            <label>c. Reported Date of Present Working Station (Village/City/Town) <span className="required">*</span></label>
            <input type="date" value={toDateStr(form.date_working_present_station)} max="2025-12-31" onChange={e => {
              const val = e.target.value;
              if (!val) { setField('date_working_present_station', ''); return; }
              setField('date_working_present_station', val);
              const p = calcServicePeriod(val);
              if (p) {
                setField('present_station_period_years', p.years);
                setField('present_station_period_months', p.months);
                setField('present_station_period_days', p.days);
              }
            }} />
          </div>
          <div className="form-group">
            <label>d. Period of Service in Present Station</label>
            <div style={{ padding: "8px 12px", background: "var(--teal-pale)", borderRadius: 6, fontSize: 13, color: "var(--teal-dark)", fontWeight: 600, border: "1px solid var(--teal-light)" }}>
              {form.date_working_present_station
                ? <><strong>{form.present_station_period_years || 0}y {form.present_station_period_months || 0}m {form.present_station_period_days || 0}d</strong> — calculated to cutoff 31/12/2025</>
                : <span style={{ color: 'var(--text-muted)' }}>Auto-calculated when date is entered above</span>}
            </div>
            <span className="autofill-note">📄 Auto-calculated from reported date above to cutoff: 31/12/2025</span>
          </div>
        </div>
        <div className="form-group" style={{ marginTop: 16 }}>
          <label>e. SR Entry Order Document (Attachment)</label>
          <p style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 8 }}>Attach Joining Order as proof of present station. (PDF, JPG, PNG — Max 10MB)</p>
          <FileUploadArea label="SR Entry / Joining Order" docType="sr_entry_doc" application={application} uploading={uploading} onFileUpload={onFileUpload} />
        </div>
      </div>
      <div className="action-bar">
        <div />
        <div style={{ display: 'flex', gap: 12 }}>
          <button className="btn btn-secondary" onClick={onSave} disabled={saving}>{saving ? '⏳' : '💾'} Save</button>
          <button className="btn btn-primary" onClick={onNext}>Next: Special Categories →</button>
        </div>
      </div>
    </div>
  );
}

// ── Section III–IX — Special Categories ──────────────────
function Section3to9({ form, setField, application, uploading, onFileUpload, onSave, saving, onBack, onNext }) {
  return (
    <div className="page-container">
      <div className="card">
        <div className="card-header">
          <div className="section-icon">🏥</div>
          <div>
            <h2 style={{ marginBottom: 2 }}>SECTIONS III–IX — Special Category Declarations</h2>
            <p style={{ fontSize: 11.5, color: 'var(--text-muted)', fontWeight: 500, margin: 0 }}>Spouse, disability, medical grounds and other special declarations</p>
          </div>
        </div>

        {/* Section III — Spouse */}
        <div style={{ marginBottom: 20 }}>
          <h3 className="subsection-title" style={{ fontSize: 13, fontWeight: 700, color: 'var(--teal-dark)', marginBottom: 12, paddingBottom: 6, borderBottom: '2px solid var(--teal-light)' }}>Section III — Spouse Employment</h3>
          <div className="form-grid">
            <div className="form-group">
              <label>Is Spouse Employed in Government Service?</label>
              <div className="radio-group">
                {['Yes', 'No'].map(v => (
                  <label key={v} className={`radio-option ${form.spouse_employed === (v === 'Yes') ? 'selected' : ''}`}>
                    <input type="radio" checked={form.spouse_employed === (v === 'Yes')} onChange={() => setField('spouse_employed', v === 'Yes')} /> {v}
                  </label>
                ))}
              </div>
            </div>
          </div>
          {form.spouse_employed && (
            <div className="form-grid">
              <div className="form-group"><label>Spouse Employer Type</label>
                <select value={form.spouse_employer_type || ''} onChange={e => setField('spouse_employer_type', e.target.value)}>
                  <option value="">— Select —</option>
                  {['State Government', 'Central Government', 'PSU', 'Local Body'].map(o => <option key={o}>{o}</option>)}
                </select>
              </div>
              <div className="form-group"><label>Spouse Name</label><input type="text" value={form.spouse_name || ''} onChange={e => setField('spouse_name', e.target.value)} /></div>
              <div className="form-group"><label>Spouse Department</label><input type="text" value={form.spouse_dept || ''} onChange={e => setField('spouse_dept', e.target.value)} /></div>
              <div className="form-group"><label>Spouse Post</label><input type="text" value={form.spouse_post || ''} onChange={e => setField('spouse_post', e.target.value)} /></div>
              <div className="form-group"><label>Spouse HQ</label><input type="text" value={form.spouse_hq || ''} onChange={e => setField('spouse_hq', e.target.value)} /></div>
            </div>
          )}
          {form.spouse_employed && (
            <div style={{ marginTop: 12 }}>
              <FileUploadArea label="Spouse Employment Proof" docType="spouse_proof" application={application} uploading={uploading} onFileUpload={onFileUpload} />
            </div>
          )}
        </div>

        {/* Section IV — Disability */}
        <div style={{ marginBottom: 20 }}>
          <h3 className="subsection-title" style={{ fontSize: 13, fontWeight: 700, color: 'var(--teal-dark)', marginBottom: 12, paddingBottom: 6, borderBottom: '2px solid var(--teal-light)' }}>Section IV — Disability</h3>
          <div className="form-grid">
            <div className="form-group">
              <label>Do you have a Disability?</label>
              <div className="radio-group">
                {['Yes', 'No'].map(v => (
                  <label key={v} className={`radio-option ${form.has_disability === (v === 'Yes') ? 'selected' : ''}`}>
                    <input type="radio" checked={form.has_disability === (v === 'Yes')} onChange={() => setField('has_disability', v === 'Yes')} /> {v}
                  </label>
                ))}
              </div>
            </div>
          </div>
          {form.has_disability && (
            <FileUploadArea label="Disability Certificate" docType="disability_cert" application={application} uploading={uploading} onFileUpload={onFileUpload} />
          )}
        </div>

        {/* Section V — MR Child */}
        <div style={{ marginBottom: 20 }}>
          <h3 className="subsection-title" style={{ fontSize: 13, fontWeight: 700, color: 'var(--teal-dark)', marginBottom: 12, paddingBottom: 6, borderBottom: '2px solid var(--teal-light)' }}>Section V — Mentally Retarded Child</h3>
          <div className="form-grid">
            <div className="form-group">
              <label>Do you have a Mentally Retarded Child?</label>
              <div className="radio-group">
                {['Yes', 'No'].map(v => (
                  <label key={v} className={`radio-option ${form.has_mentally_retarded_child === (v === 'Yes') ? 'selected' : ''}`}>
                    <input type="radio" checked={form.has_mentally_retarded_child === (v === 'Yes')} onChange={() => setField('has_mentally_retarded_child', v === 'Yes')} /> {v}
                  </label>
                ))}
              </div>
            </div>
          </div>
          {form.has_mentally_retarded_child && (
            <FileUploadArea label="MR Child Certificate" docType="mentally_retarded_cert" application={application} uploading={uploading} onFileUpload={onFileUpload} />
          )}
        </div>

        {/* Section VI — Widow */}
        <div style={{ marginBottom: 20 }}>
          <h3 className="subsection-title" style={{ fontSize: 13, fontWeight: 700, color: 'var(--teal-dark)', marginBottom: 12, paddingBottom: 6, borderBottom: '2px solid var(--teal-light)' }}>Section VI — Widow / Widower</h3>
          <div className="form-grid">
            <div className="form-group">
              <label>Are you a Widow / Widower?</label>
              <div className="radio-group">
                {['Yes', 'No'].map(v => (
                  <label key={v} className={`radio-option ${form.is_widow === (v === 'Yes') ? 'selected' : ''}`}>
                    <input type="radio" checked={form.is_widow === (v === 'Yes')} onChange={() => setField('is_widow', v === 'Yes')} /> {v}
                  </label>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Section VII — Medical Grounds */}
        <div style={{ marginBottom: 20 }}>
          <h3 className="subsection-title" style={{ fontSize: 13, fontWeight: 700, color: 'var(--teal-dark)', marginBottom: 12, paddingBottom: 6, borderBottom: '2px solid var(--teal-light)' }}>Section VII — Medical Grounds (Cancer, Neurosurgery, Kidney/Liver Transplantation, Open Heart Surgery, Bone TB)</h3>
          <div className="form-grid">
            <div className="form-group">
              <label>Do you have Medical Grounds?</label>
              <div className="radio-group">
                {['Yes', 'No'].map(v => (
                  <label key={v} className={`radio-option ${form.has_medical_grounds === (v === 'Yes') ? 'selected' : ''}`}>
                    <input type="radio" checked={form.has_medical_grounds === (v === 'Yes')} onChange={() => setField('has_medical_grounds', v === 'Yes')} /> {v}
                  </label>
                ))}
              </div>
            </div>
            {form.has_medical_grounds && (
              <div className="form-group">
                <label>Disease Category <span className="required">*</span></label>
                <select value={form.medical_disease_category || ''} onChange={e => setField('medical_disease_category', e.target.value)}>
                  <option value="">— Select —</option>
                  {['Cancer', 'Neurosurgery', 'Kidney Transplantation', 'Liver Transplantation', 'Open Heart Surgery', 'Bone TB'].map(o => <option key={o}>{o}</option>)}
                </select>
              </div>
            )}
            {form.has_medical_grounds && (
              <div className="form-group">
                <label>Medical Grounds Person</label>
                <select value={form.medical_grounds_person || ''} onChange={e => setField('medical_grounds_person', e.target.value)}>
                  <option value="">— Select —</option>
                  {['Self', 'Spouse', 'Dependent Child', 'Dependent Parent'].map(o => <option key={o}>{o}</option>)}
                </select>
              </div>
            )}
          </div>
          {form.has_medical_grounds && (
            <FileUploadArea label="Medical Certificate" docType="medical_cert" application={application} uploading={uploading} onFileUpload={onFileUpload} />
          )}
        </div>

        {/* Section VIII — Disciplinary */}
        <div style={{ marginBottom: 20 }}>
          <h3 className="subsection-title" style={{ fontSize: 13, fontWeight: 700, color: 'var(--teal-dark)', marginBottom: 12, paddingBottom: 6, borderBottom: '2px solid var(--teal-light)' }}>Section VIII — Disciplinary Case</h3>
          <div className="form-grid">
            <div className="form-group">
              <label>Is there any Disciplinary Case pending?</label>
              <div className="radio-group">
                {['Yes', 'No'].map(v => (
                  <label key={v} className={`radio-option ${form.has_disciplinary_case === (v === 'Yes') ? 'selected' : ''}`}>
                    <input type="radio" checked={form.has_disciplinary_case === (v === 'Yes')} onChange={() => setField('has_disciplinary_case', v === 'Yes')} /> {v}
                  </label>
                ))}
              </div>
            </div>
            {form.has_disciplinary_case && (
              <div className="form-group span2">
                <label>Details of Disciplinary Case</label>
                <textarea value={form.disciplinary_case_details || ''} onChange={e => setField('disciplinary_case_details', e.target.value)} rows={3} placeholder="Provide details of the disciplinary case..." />
              </div>
            )}
          </div>
        </div>

        {/* Section IX — Census */}
        <div style={{ marginBottom: 20 }}>
          <h3 className="subsection-title" style={{ fontSize: 13, fontWeight: 700, color: 'var(--teal-dark)', marginBottom: 12, paddingBottom: 6, borderBottom: '2px solid var(--teal-light)' }}>Section IX — Census Duty</h3>
          <div className="form-grid">
            <div className="form-group">
              <label>Are you on Census Duty?</label>
              <div className="radio-group">
                {['Yes', 'No'].map(v => (
                  <label key={v} className={`radio-option ${form.has_census_duty === (v === 'Yes') ? 'selected' : ''}`}>
                    <input type="radio" checked={form.has_census_duty === (v === 'Yes')} onChange={() => setField('has_census_duty', v === 'Yes')} /> {v}
                  </label>
                ))}
              </div>
            </div>
          </div>
          {form.has_census_duty && (
            <FileUploadArea label="Census Appointment Order" docType="census_order" application={application} uploading={uploading} onFileUpload={onFileUpload} />
          )}
        </div>
      </div>

      <div className="action-bar">
        <button className="btn btn-secondary" onClick={onBack}>← Back</button>
        <div style={{ display: 'flex', gap: 12 }}>
          <button className="btn btn-secondary" onClick={onSave} disabled={saving}>{saving ? '⏳' : '💾'} Save</button>
          <button className="btn btn-primary" onClick={onNext}>Next: Preferences →</button>
        </div>
      </div>
    </div>
  );
}

// ── Section X — Preferences ───────────────────────────────
function SectionPreferences({ form, preferences, setPreferences, availableULBs, declarationChecked, setDeclarationChecked, saving, onSave, onSubmit, onNext, onBack, application, masters }) {
  const { user } = useAuth();
  const [pdfLoading, setPdfLoading] = React.useState(false);
  const [showVacancyNotice, setShowVacancyNotice] = React.useState(true);

  const updatePref = (idx, key, val) => {
    setPreferences(prefs => prefs.map((p, i) => i === idx ? { ...p, [key]: val } : p));
  };

  const addPref = () => {
    if (preferences.length >= 5) { toast.error('Maximum 5 preferences allowed'); return; }
    setPreferences(prefs => [...prefs, { priority: prefs.length + 1, ulbId: '', orgId: '', reason: '' }]);
  };

  const removePref = (idx) => {
    setPreferences(prefs => prefs.filter((_, i) => i !== idx).map((p, i) => ({ ...p, priority: i + 1 })));
  };

  const handleDownloadPDF = async () => {
    if (!application?.id) { toast.error('Save your application first'); return; }
    setPdfLoading(true);
    try {
      const token = localStorage.getItem('tg_token');
      const res = await fetch(`/api/v1/applications/${application.id}/pdf`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error('PDF generation failed');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `Transfer_Application_${form.employee_id_display || 'TG'}_2026.pdf`; a.click();
      URL.revokeObjectURL(url);
      toast.success('PDF downloaded');
    } catch (err) { toast.error('PDF generation failed'); }
    finally { setPdfLoading(false); }
  };

  const isDTCPPref = String(user?.deptId) === '3';
  const ownId = isDTCPPref ? String(form.org_id || '') : String(form.local_cadre_ulb_id || '');

  const VacancyNoticeModal = () => (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: 20 }}>
      <div style={{ background: '#fff', borderRadius: 12, padding: 28, maxWidth: 480, width: '100%', boxShadow: '0 8px 32px rgba(0,0,0,.25)', textAlign: 'center' }}>
        <div style={{ fontSize: 36, marginBottom: 12 }}>⚠️</div>
        <div style={{ fontSize: 16, fontWeight: 800, color: '#b71c1c', marginBottom: 12 }}>Important Notice</div>
        <div style={{ fontSize: 14, color: '#333', lineHeight: 1.7, marginBottom: 20, background: '#fff3e0', border: '1.5px solid #e65100', borderRadius: 8, padding: '12px 16px', fontWeight: 500 }}>
          Officers shall submit their preferences strictly against the available vacancies only.
        </div>
        <button onClick={() => setShowVacancyNotice(false)} style={{ background: '#1a237e', color: '#fff', border: 'none', borderRadius: 8, padding: '10px 32px', fontSize: 14, fontWeight: 700, cursor: 'pointer', width: '100%' }}>
          I Understand — Proceed to Preferences
        </button>
      </div>
    </div>
  );

  return (
    <div className="page-container">
      {showVacancyNotice && <VacancyNoticeModal />}

      {!showVacancyNotice && (
        <div style={{ background: '#fff3e0', border: '1.5px solid #e65100', borderRadius: 8, padding: '10px 16px', marginBottom: 14, display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 18 }}>⚠️</span>
          <span style={{ fontSize: 13, color: '#bf360c', fontWeight: 600 }}>Officers shall submit their preferences strictly against the available vacancies only.</span>
        </div>
      )}

      <div className="card">
        <div className="card-header">
          <div className="section-icon">📍</div>
          <div>
            <h2 style={{ marginBottom: 2 }}>Section X — Transfer Preferences</h2>
            <p style={{ fontSize: 11.5, color: 'var(--text-muted)', fontWeight: 500, margin: 0 }}>Select up to 5 preferred transfer locations in order of preference</p>
          </div>
        </div>

        <div style={{ marginBottom: 16 }}>
          {preferences.map((pref, idx) => (
            <div key={idx} style={{ display: 'grid', gridTemplateColumns: '32px 1fr 1fr auto', gap: 10, alignItems: 'center', marginBottom: 10, padding: '10px 12px', background: '#f8f8f8', borderRadius: 8 }}>
              <div style={{ fontWeight: 800, color: '#1a237e', textAlign: 'center', fontSize: 16 }}>{pref.priority}</div>
              <select value={isDTCPPref ? (pref.orgId || '') : (pref.ulbId || '')} onChange={e => updatePref(idx, isDTCPPref ? 'orgId' : 'ulbId', e.target.value)} style={{ padding: '8px 10px', borderRadius: 6, border: '1px solid #e0e0e0', fontSize: 13, fontFamily: 'inherit' }}>
                <option value="">— Select location —</option>
                {availableULBs.filter(u => String(u.id) !== ownId && !preferences.some((p, pi) => pi !== idx && (isDTCPPref ? String(p.orgId) : String(p.ulbId)) === String(u.id))).map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
              </select>
              <input type="text" value={pref.reason || ''} onChange={e => updatePref(idx, 'reason', e.target.value)} placeholder="Reason (optional)" style={{ padding: '8px 10px', borderRadius: 6, border: '1px solid #e0e0e0', fontSize: 13, fontFamily: 'inherit' }} />
              {preferences.length > 1 && <button onClick={() => removePref(idx)} style={{ background: '#ffebee', border: 'none', color: '#c62828', borderRadius: 6, padding: '6px 10px', cursor: 'pointer', fontSize: 16 }}>✕</button>}
            </div>
          ))}
          {preferences.length < 5 && (
            <button onClick={addPref} style={{ background: '#e8eaf6', border: '1px dashed #3949ab', color: '#1a237e', borderRadius: 8, padding: '8px 20px', cursor: 'pointer', fontSize: 13, fontWeight: 600, width: '100%' }}>+ Add Preference</button>
          )}
        </div>

        {/* Declaration */}
        <div style={{ background: '#fff8e1', border: '1px solid #ffe082', borderRadius: 8, padding: 14, marginBottom: 16 }}>
          <label style={{ display: 'flex', gap: 10, alignItems: 'flex-start', cursor: 'pointer', fontSize: 13, color: '#333', lineHeight: 1.6 }}>
            <input type="checkbox" checked={declarationChecked} onChange={e => setDeclarationChecked(e.target.checked)} style={{ marginTop: 3, accentColor: '#1a237e', width: 16, height: 16, flexShrink: 0 }} />
            I hereby declare that all the information furnished in this application is true and correct to the best of my knowledge and belief. I understand that furnishing false information is liable for disciplinary action.
          </label>
        </div>

        <div style={{ display: 'flex', gap: 10 }}>
          <button onClick={handleDownloadPDF} disabled={pdfLoading || !application?.id} style={{ flex: 1, padding: '10px 0', background: '#e8eaf6', border: '1px solid #3949ab', color: '#1a237e', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
            {pdfLoading ? '⏳ Generating...' : '📄 Preview PDF'}
          </button>
        </div>
      </div>

      <div className="action-bar">
        <button className="btn btn-secondary" onClick={onBack}>← Back</button>
        <div style={{ display: 'flex', gap: 12 }}>
          <button className="btn btn-secondary" onClick={() => onSave(false)} disabled={saving}>{saving ? '⏳' : '💾'} Save</button>
          <button className="btn btn-primary" onClick={onNext}>Next: Upload & Submit →</button>
        </div>
      </div>
    </div>
  );
}

// ── Section XI — Upload & Submit ──────────────────────────
function SectionUploadSubmit({ form, application, uploading, onFileUpload, saving, onSave, onSubmit, onBack }) {
  return (
    <div className="page-container">
      <div className="card">
        <div className="card-header">
          <div className="section-icon">📤</div>
          <div>
            <h2 style={{ marginBottom: 2 }}>Section XI — Upload Signed Copy & Submit</h2>
            <p style={{ fontSize: 11.5, color: 'var(--text-muted)', fontWeight: 500, margin: 0 }}>Upload your signed application copy and submit</p>
          </div>
        </div>
        <div style={{ marginBottom: 20 }}>
          <p style={{ fontSize: 13, color: '#555', marginBottom: 14, lineHeight: 1.6 }}>Download the application PDF, print it, sign it, and upload the signed copy below before final submission.</p>
          <FileUploadArea label="Signed Application Copy" docType="signed_copy" application={application} uploading={uploading} onFileUpload={onFileUpload} />
        </div>
        {application?.status === 'draft' && (
          <div style={{ background: '#e8f5e9', border: '1px solid #a5d6a7', borderRadius: 8, padding: 14, marginBottom: 16 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: '#2e7d32', marginBottom: 6 }}>Ready to Submit</div>
            <div style={{ fontSize: 13, color: '#555' }}>Once submitted, you cannot edit your application. Ensure all details are correct before submitting.</div>
          </div>
        )}
        {application?.status === 'submitted' && (
          <div style={{ background: '#e3f2fd', border: '1px solid #90caf9', borderRadius: 8, padding: 14 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: '#1565c0', marginBottom: 4 }}>✅ Application Submitted</div>
            <div style={{ fontSize: 13, color: '#555' }}>Your application has been submitted successfully and is under review.</div>
          </div>
        )}
      </div>
      <div className="action-bar">
        <button className="btn btn-secondary" onClick={onBack}>← Back</button>
        <div style={{ display: 'flex', gap: 12 }}>
          <button className="btn btn-secondary" onClick={onSave} disabled={saving}>{saving ? '⏳' : '💾'} Save</button>
          {application?.status !== 'submitted' && (
            <button className="btn btn-success" onClick={onSubmit} disabled={saving} style={{ background: '#2e7d32', color: '#fff', border: 'none', borderRadius: 8, padding: '10px 24px', fontSize: 14, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
              ✅ Submit Application
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Section Orders ────────────────────────────────────────
// ── Section Orders ────────────────────────────────────────
// Shows the logged-in employee's transfer order (if released).
// Fetches GET /api/v1/employee/my-transfer-order on mount.
// Streams the PDF via GET /api/v1/employee/my-transfer-order/pdf
function SectionOrders({ onBack }) {
  const [loading, setLoading] = React.useState(true);
  const [order, setOrder]     = React.useState(null);
  const [error, setError]     = React.useState(null);
  const [downloading, setDownloading] = React.useState(false);

  React.useEffect(() => {
    const fetchOrder = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await fetch('/api/v1/employee/my-transfer-order', {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.status === 404) {
          // No order issued yet — treat as "not yet"
          setOrder(null);
        } else if (!res.ok) {
          const j = await res.json().catch(() => ({}));
          setError(j.error || `Server error (${res.status})`);
        } else {
          const data = await res.json();
          setOrder(data);
        }
      } catch (e) {
        setError('Network error — please try again');
      } finally {
        setLoading(false);
      }
    };
    fetchOrder();
  }, []);

  const downloadPdf = async () => {
    if (downloading) return;
    setDownloading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch('/api/v1/employee/my-transfer-order/pdf', {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        alert('Failed to download PDF: ' + (j.error || res.status));
        return;
      }
      const blob = await res.blob();
      const url  = window.URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href = url;
      a.download = `Transfer_Order_${(order?.order_number || 'order').replace(/\//g, '-')}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 1000);
    } catch (e) {
      alert('Download failed: ' + e.message);
    } finally {
      setDownloading(false);
    }
  };

  const fmtDate = (s) => {
    if (!s) return '—';
    try {
      const d = new Date(s);
      return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) +
             ', ' + d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
    } catch { return s; }
  };

  return (
    <div className="page-container">
      <div className="card">
        <div className="card-header">
          <div className="section-icon">📋</div>
          <div><h2>Transfer Orders</h2></div>
        </div>

        {loading && (
          <div style={{ padding: 24, textAlign: 'center', color: '#666', fontSize: 14 }}>
            ⏳ Loading your transfer order...
          </div>
        )}

        {!loading && error && (
          <div style={{ padding: 20, color: '#c62828', fontSize: 14, background: '#ffebee', borderRadius: 8, margin: 16 }}>
            ⚠ {error}
          </div>
        )}

        {!loading && !error && !order && (
          <div style={{ padding: 24, textAlign: 'center', background: '#f5f5f5', borderRadius: 8, margin: 16 }}>
            <div style={{ fontSize: 40, marginBottom: 8 }}>⏳</div>
            <div style={{ fontSize: 15, fontWeight: 600, color: '#1a237e', marginBottom: 4 }}>
              No transfer order issued yet
            </div>
            <div style={{ fontSize: 13, color: '#666' }}>
              Your transfer order will appear here once sanctioned by the approving officer.
            </div>
          </div>
        )}

        {!loading && !error && order && (
          <div style={{ padding: 16 }}>
            <div style={{
              background: '#e8f5e9',
              border: '1px solid #a5d6a7',
              borderRadius: 10,
              padding: 16,
              marginBottom: 16,
            }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: '#2e7d32', marginBottom: 4 }}>
                ✅ Transfer Order Issued
              </div>
              <div style={{ fontSize: 13, color: '#555' }}>
                Your transfer order has been issued. Download your official proceedings below.
              </div>
            </div>

            <div style={{
              background: '#f5f5f5',
              borderRadius: 10,
              padding: 16,
              marginBottom: 16,
            }}>
              <div style={{ fontSize: 12, color: '#666', marginBottom: 10, fontWeight: 700 }}>
                Order Details
              </div>
              {[
                ['Order Number',          order.order_number],
                ['Name',                  order.full_name],
                ['Present ULB',           order.present_ulb],
                ['Present Designation',   order.present_designation],
                ['Transferred to ULB',    order.transferred_ulb],
                ['New Designation',       order.transfer_designation],
                ['Released On',           fmtDate(order.released_at)],
              ].filter(([, v]) => v).map(([label, value]) => (
                <div
                  key={label}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    fontSize: 13,
                    padding: '6px 0',
                    borderBottom: '1px solid #eee',
                  }}
                >
                  <span style={{ color: '#888' }}>{label}</span>
                  <span style={{ color: '#333', fontWeight: 500, textAlign: 'right', maxWidth: '60%' }}>
                    {value}
                  </span>
                </div>
              ))}
            </div>

            <button
              onClick={downloadPdf}
              disabled={downloading}
              style={{
                width: '100%',
                background: downloading ? '#90a4ae' : '#1a237e',
                color: '#fff',
                border: 'none',
                borderRadius: 8,
                padding: '14px 24px',
                fontSize: 15,
                fontWeight: 700,
                cursor: downloading ? 'wait' : 'pointer',
                fontFamily: 'inherit',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 8,
              }}
            >
              {downloading ? '⏳ Downloading...' : '📄 Download Transfer Order PDF'}
            </button>

            {order.whatsapp_sent && (
              <div style={{
                marginTop: 12,
                background: '#e3f2fd',
                border: '1px solid #90caf9',
                borderRadius: 8,
                padding: 12,
                fontSize: 13,
                color: '#1565c0',
              }}>
                📱 This order was sent to your WhatsApp on {fmtDate(order.whatsapp_sent_at)}
              </div>
            )}
          </div>
        )}
      </div>

      <div className="action-bar">
        <button className="btn btn-secondary" onClick={onBack}>← Back</button>
      </div>
    </div>
  );
}



// ═══════════════════════════════════════════════════════════
//  WHATSAPP CELL — Inline "Send WhatsApp" button per row
// ═══════════════════════════════════════════════════════════
function WhatsAppCell({ order, onUpdate }) {
  const [sending, setSending] = React.useState(false);

  const status = order.whatsapp_status;
  const sent = order.whatsapp_sent;

  const send = async () => {
    if (sending) return;
    if (!window.confirm('Send Transfer Order via WhatsApp to ' + (order.mobile_no || '???') + '?')) return;
    setSending(true);
    try {
      const token = localStorage.getItem('tg_token');
      const r = await fetch('/api/v1/transfer-orders/' + order.id + '/send-whatsapp', {
        method: 'POST',
        headers: { Authorization: 'Bearer ' + token, 'Content-Type': 'application/json' },
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.error || 'Send failed');
      const stRes = await fetch('/api/v1/transfer-orders/' + order.id + '/whatsapp-status', {
        headers: { Authorization: 'Bearer ' + token }
      });
      if (stRes.ok) onUpdate(await stRes.json());
    } catch (e) {
      alert('⚠ WhatsApp send failed:\n\n' + e.message);
    } finally {
      setSending(false);
    }
  };

  if (sent && status === 'read') {
    return (
      <div style={{ fontSize: 11, color: '#1976d2', fontWeight: 600 }} title="Read by recipient">
        ✓✓ Read
        <button onClick={send} disabled={sending} style={{ marginLeft: 6, padding: '3px 8px', fontSize: 10, background: '#fff', border: '1px solid #1976d2', color: '#1976d2', borderRadius: 3, cursor: 'pointer' }}>↻</button>
      </div>
    );
  }
  if (sent && status === 'delivered') {
    return (
      <div style={{ fontSize: 11, color: '#2e7d32', fontWeight: 600 }} title="Delivered">
        ✓✓ Delivered
        <button onClick={send} disabled={sending} style={{ marginLeft: 6, padding: '3px 8px', fontSize: 10, background: '#fff', border: '1px solid #2e7d32', color: '#2e7d32', borderRadius: 3, cursor: 'pointer' }}>↻</button>
      </div>
    );
  }
  if (sent && (status === 'sent' || !status)) {
    return (
      <div style={{ fontSize: 11, color: '#2e7d32', fontWeight: 600 }} title={'Sent at ' + (order.whatsapp_sent_at || '')}>
        ✓ Sent
        <button onClick={send} disabled={sending} style={{ marginLeft: 6, padding: '3px 8px', fontSize: 10, background: '#fff', border: '1px solid #2e7d32', color: '#2e7d32', borderRadius: 3, cursor: 'pointer' }}>↻</button>
      </div>
    );
  }
  if (status === 'failed') {
    return (
      <div title={order.whatsapp_error || 'Send failed'}>
        <button onClick={send} disabled={sending} style={{ padding: '6px 10px', background: '#c62828', color: '#fff', border: 'none', borderRadius: 4, fontSize: 11, fontWeight: 600, cursor: sending ? 'wait' : 'pointer' }}>
          {sending ? '⏳ Sending...' : '❌ Retry'}
        </button>
      </div>
    );
  }
  return (
    <button onClick={send} disabled={sending} style={{ padding: '6px 12px', background: '#25D366', color: '#fff', border: 'none', borderRadius: 4, fontSize: 11, fontWeight: 600, cursor: sending ? 'wait' : 'pointer', whiteSpace: 'nowrap' }} title="Send Transfer Order via WhatsApp">
      {sending ? '⏳ Sending...' : '📱 Send'}
    </button>
  );
}

// ═══════════════════════════════════════════════════════════
//  VIEW TRANSFER ORDERS — Admin list page
// ═══════════════════════════════════════════════════════════
function ViewTransferOrders() {
  const [orders, setOrders] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [search, setSearch] = React.useState('');
  const [error, setError] = React.useState(null);

  React.useEffect(() => {
    const fetchOrders = async () => {
      try {
        const token = localStorage.getItem('tg_token');
        const res = await fetch('/api/v1/transfer-orders', {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (!res.ok) throw new Error('Failed to fetch');
        const data = await res.json();
        setOrders(data.data || []);
      } catch (e) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    };
    fetchOrders();
  }, []);

  const filtered = orders.filter(o => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      (o.order_number || '').toLowerCase().includes(q) ||
      (o.emp_id || '').toLowerCase().includes(q) ||
      (o.full_name || '').toLowerCase().includes(q) ||
      (o.transferred_ulb || '').toLowerCase().includes(q) ||
      (o.present_ulb || '').toLowerCase().includes(q)
    );
  });

  const downloadCSV = () => {
    const headers = ['Order Number', 'EmpId', 'Full Name', 'Mobile', 'Present Designation', 'Present ULB', 'Multi-Zone', 'Transfer Designation', 'Approving Authority', 'Transferred ULB', 'Released At'];
    const rows = filtered.map(o => [
      o.order_number, o.emp_id, o.full_name, o.mobile_no,
      o.present_designation, o.present_ulb, o.multi_zone,
      o.transfer_designation, o.approving_authority, o.transferred_ulb,
      o.created_at ? new Date(o.created_at).toLocaleString('en-IN') : ''
    ]);
    const csv = [headers, ...rows].map(r => r.map(c => `"${(c || '').toString().replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `transfer_orders_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div style={{ padding: 24, background: '#f5f7fa', minHeight: '100vh' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h2 style={{ fontSize: 22, fontWeight: 800, color: '#1a237e', margin: 0 }}>📋 View Transfer Orders</h2>
          <p style={{ fontSize: 13, color: '#666', marginTop: 4 }}>All released transfer orders — total: {orders.length}</p>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="🔍 Search by Order#, EmpId, Name, ULB..."
            style={{ padding: '8px 14px', border: '1.5px solid #d0d7e2', borderRadius: 6, fontSize: 13, width: 320, fontFamily: 'inherit' }}
          />
          <button
            onClick={downloadCSV}
            disabled={!filtered.length}
            style={{ padding: '8px 16px', background: '#2e7d32', color: '#fff', border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: filtered.length ? 'pointer' : 'not-allowed', opacity: filtered.length ? 1 : 0.5 }}
          >
            ⬇ Download CSV
          </button>
        </div>
      </div>

      {loading && <div style={{ padding: 40, textAlign: 'center', color: '#666' }}>Loading transfer orders...</div>}
      {error && <div style={{ padding: 16, background: '#fee', border: '1px solid #fcc', borderRadius: 6, color: '#c00' }}>⚠ Error: {error}</div>}

      {!loading && !error && (
        <div style={{ background: '#fff', borderRadius: 10, border: '1px solid #e0e0e0', overflow: 'hidden' }}>
          {filtered.length === 0 ? (
            <div style={{ padding: 60, textAlign: 'center', color: '#999' }}>
              {orders.length === 0 ? 'No transfer orders released yet. Use "Release Transfer Orders" to create them.' : 'No orders match your search.'}
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ background: '#f5f7fa', borderBottom: '2px solid #d0d7e2' }}>
                    <th style={{ padding: '12px 10px', textAlign: 'left', fontWeight: 700, color: '#1a237e', whiteSpace: 'nowrap' }}>#</th>
                    <th style={{ padding: '12px 10px', textAlign: 'left', fontWeight: 700, color: '#1a237e', whiteSpace: 'nowrap' }}>Order Number</th>
                    <th style={{ padding: '12px 10px', textAlign: 'left', fontWeight: 700, color: '#1a237e' }}>EmpId</th>
                    <th style={{ padding: '12px 10px', textAlign: 'left', fontWeight: 700, color: '#1a237e' }}>Full Name</th>
                    <th style={{ padding: '12px 10px', textAlign: 'left', fontWeight: 700, color: '#1a237e' }}>Mobile</th>
                    <th style={{ padding: '12px 10px', textAlign: 'left', fontWeight: 700, color: '#1a237e' }}>Present ULB</th>
                    <th style={{ padding: '12px 10px', textAlign: 'left', fontWeight: 700, color: '#1a237e' }}>→ Transferred ULB</th>
                    <th style={{ padding: '12px 10px', textAlign: 'left', fontWeight: 700, color: '#1a237e' }}>Designation</th>
                    <th style={{ padding: '12px 10px', textAlign: 'left', fontWeight: 700, color: '#1a237e' }}>Authority</th>
                    <th style={{ padding: '12px 10px', textAlign: 'left', fontWeight: 700, color: '#1a237e', whiteSpace: 'nowrap' }}>Released At</th>
                    <th style={{ padding: '12px 10px', textAlign: 'center', fontWeight: 700, color: '#1a237e', whiteSpace: 'nowrap' }}>Action</th>
                    <th style={{ padding: '12px 10px', textAlign: 'center', fontWeight: 700, color: '#1a237e', whiteSpace: 'nowrap' }}>WhatsApp</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((o, i) => (
                    <tr key={o.id} style={{ borderBottom: '1px solid #eee', background: i % 2 === 0 ? '#fff' : '#fafbfc' }}>
                      <td style={{ padding: '10px', color: '#666' }}>{i + 1}</td>
                      <td style={{ padding: '10px', fontFamily: 'monospace', color: '#1a237e', fontWeight: 600 }}>{o.order_number}</td>
                      <td style={{ padding: '10px', fontWeight: 600 }}>{o.emp_id}</td>
                      <td style={{ padding: '10px' }}>{o.full_name}</td>
                      <td style={{ padding: '10px', fontFamily: 'monospace', fontSize: 12 }}>{o.mobile_no || '—'}</td>
                      <td style={{ padding: '10px' }}>{o.present_ulb || '—'}</td>
                      <td style={{ padding: '10px', fontWeight: 600, color: '#2e7d32' }}>{o.transferred_ulb || '—'}</td>
                      <td style={{ padding: '10px', fontSize: 12 }}>{o.transfer_designation || '—'}</td>
                      <td style={{ padding: '10px', fontSize: 12 }}>{o.approving_authority || '—'}</td>
                      <td style={{ padding: '10px', fontSize: 12, color: '#666', whiteSpace: 'nowrap' }}>
                        {o.created_at ? new Date(o.created_at).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—'}
                      </td>
                      <td style={{ padding: '10px', textAlign: 'center', whiteSpace: 'nowrap' }}>
                        <button
                          onClick={async () => {
                            try {
                              const token = localStorage.getItem('tg_token');
                              const res = await fetch(`/api/v1/transfer-orders/${o.id}/pdf`, {
                                headers: { Authorization: `Bearer ${token}` }
                              });
                              if (!res.ok) {
                                const err = await res.json().catch(() => ({ error: 'PDF generation failed' }));
                                throw new Error(err.error || 'PDF generation failed');
                              }
                              const blob = await res.blob();
                              const url = URL.createObjectURL(blob);
                              const a = document.createElement('a');
                              a.href = url;
                              a.download = `Transfer_Order_${o.order_number.replace(/[\/]/g, '-')}.pdf`;
                              a.click();
                              URL.revokeObjectURL(url);
                            } catch (e) {
                              alert('⚠ Failed to download PDF:\n\n' + e.message);
                            }
                          }}
                          style={{ padding: '6px 14px', background: '#00695c', color: '#fff', border: 'none', borderRadius: 4, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}
                          title="Download Official LR-based Transfer Order PDF"
                        >
                          📄 PDF
                        </button>
                      </td>
                      <td style={{ padding: '10px', textAlign: 'center', whiteSpace: 'nowrap' }}>
                        <WhatsAppCell order={o} onUpdate={(updated) => {
                          setOrders(prev => prev.map(x => x.id === updated.id ? { ...x, ...updated } : x));
                        }} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
